import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  expand,
  filter,
  finalize,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  reduce,
  retry,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { catchError } from 'rxjs/operators';

/** Models */
import { UserModel } from '../../models/user-model';
import { SidebarMenuItem } from '../../models/common';
import { ANNOUNCEMENT_ANY_AUTHOR_ROLE_IDS, RoleConstant } from '../../models/role-constant';
import { ROLES_PAGE_URL } from '../../models/role.model';
import { UserRoleModel } from '../../models/user-role-model';

/** Services */
import { AuthService } from '../../services/auth.service';
import { ContextService } from '../../services/context.service';
import { AiPreferenceService } from '../../services/ai-preference.service';
import { ApPermissionService } from '../../services/ap-permission.service';
import { ServiceRequestType } from 'src/app/models/service-request-type';
import {
  NotificationState,
  SearchRequest,
} from 'src/app/models/notification-search';
import { FormControl } from '@angular/forms';
import { NotificationsService } from 'src/app/services/notifications.service';
import { AnnouncementService } from 'src/app/services/announcement.service';
import { NotificationsResponse } from 'src/app/models/notifications-response';
import { NotificationEvent } from 'src/app/models/notification-event.model';
import { AnnouncementListItem } from 'src/app/models/response/announcement-search-response.model';
import { MessagingService } from 'src/app/services/messaging.service';
import { SseService } from 'src/app/services/sse.service';
import { UserContextRefreshService } from 'src/app/services/user-context-refresh.service';
import { AppRealtimeEventsService } from 'src/app/services/app-realtime-events.service';
import { ORGANIZATION_SETTINGS_URL } from 'src/app/models/organization.model';
import { environment } from 'src/environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { toKebabCase } from 'src/app/utils/string-utils';
import { AppModuleKey, AppPermissions } from 'src/app/models/user-permission-model';
import { CompanyModel } from 'src/app/models/company-model';
import { CompanyService } from 'src/app/services/company.service';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BulkApRequestsModalComponent } from 'src/app/pages/account-payable/bulk-ap-requests-modal/bulk-ap-requests-modal.component';
import { ChangePasswordModalComponent } from 'src/app/components/change-password-modal/change-password-modal.component';
import {
  AP_BULK_INVOICE_ALLOWED_EXTENSIONS,
  ApBulkInvoiceUploadService,
} from 'src/app/services/ap-bulk-invoice-upload.service';
import { ShellChromeService } from '../../services/shell-chrome.service';
import { ImpersonationService } from '../../services/impersonation.service';
import { OrganizationService } from '../../services/organization.service';
import { CompanyModuleName } from '../../models/company-minimal-model';
import { MdAiPanelService } from '../../services/md-ai-panel.service';
import { DashboardViewKey } from 'src/app/pages/dashboard/dashboard-tabs/dashboard-module-map';
import { ROLE_ID_TO_NAME_MAP } from 'src/app/utils/role-id-to-name.map';
import { withPreservedUserRoles, persistFullUserRolesCache } from 'src/app/utils/user-roles-merge.util';
import { ROLE_PERMISSIONS_MAP } from 'src/app/utils/role-permissions-map';
import { AccountsPayableAction } from 'src/app/models/accounts-payable-actions.model';
import {
  DROPDOWN_COORDINATOR_IDS,
  DropdownCoordinatorService,
} from 'src/app/services/dropdown-coordinator.service';
import {
  ApNavGroup,
  ExpenseNavGroup,
  PageNavGroup,
  RmNavGroup,
  VendorNavGroup,
} from 'src/app/models/company-permission.model';
import {
  isApSubMenuAction,
  isApSubMenuRoute,
  isApSubMenuRouteActive,
  isExpenseSubMenuRouteActive,
  findPreferredApLandingSubMenu,
  getPageUrlQueryParams,
  normalizePageUrl,
  normalizeRoutePath,
  parsePageUrlParts,
  toRouterPath,
} from 'src/app/utils/company-permission.util';
import { UserService } from 'src/app/services/user.service';
import { ExpensePermissionService } from 'src/app/services/expense-permission.service';
import { VendorPermissionService } from 'src/app/services/vendor-permission.service';
import { RmPermissionService } from 'src/app/services/rm-permission.service';
import { isVendorSubMenuRouteActive } from 'src/app/utils/vendor-list-tab.util';
import { ExpenseAction } from 'src/app/models/expense-actions.model';
import { SessionStorageService } from 'src/app/services/sessions-storage.service';
import { SessionStorageConstants } from 'src/app/models/common/session-storage-constants';
import {
  getRepairNavGroup,
  hasRepairSidebar,
  isRmSubMenuRouteActive,
} from 'src/app/utils/rm-list-tab.util';
import {
  getInviteIdFromUrl,
  isVendorCreateRoute,
  isVendorPaymentDetailsRoute,
} from 'src/app/utils/invite-id.util';

declare var bootstrap: any;

/** Sidebar items that belong to an organization module. */
const NAV_MODULES: Partial<Record<string, CompanyModuleName>> = {
  ap: 'AccountsPayable',
  expense: 'ExpenseManagement',
  repair: 'RepairAndMaintenance',
  mdAi: 'Ai',
};

/** Quick Actions that belong to an organization module. */
const QUICK_ACTION_MODULES: Partial<Record<string, CompanyModuleName>> = {
  'create-accounts-payable': 'AccountsPayable',
  'create-bulk-ap-request': 'AccountsPayable',
  'create-expense-request': 'ExpenseManagement',
  'create-rm-request': 'RepairAndMaintenance',
};

/**
 * Quick Actions a System Admin keeps inside an organization (AC 7.5), plus Create Asset since Asset
 * Management is in the organization's sidebar.
 */
/** Quick Actions in the System Admin portal (outside an organization), matching its sidebar: Organizations, Users. */
const PLATFORM_QUICK_ACTION_IDS: readonly string[] = ['create-company', 'create-user'];

const IMPERSONATION_QUICK_ACTION_IDS: readonly (string | undefined)[] = [
  'create-company',
  'create-location',
  'create-user',
  'create-vendor',
  'create-asset',
];

@Component({
  selector: 'auth-layout',
  templateUrl: './auth.layout.html',
  styleUrls: ['./auth.layout.scss'],
  providers: [],
  host: {
    '[class.has-announcement-banner]': 'bannerAnnouncements.length > 0',
  },
})
export class AuthLayout implements OnInit, OnDestroy {
  @ViewChild('dropdownToggle', { static: false }) dropdownToggle!: ElementRef;
  @ViewChild('profileDropdownToggle', { static: false }) profileDropdownToggle!: ElementRef;
  @ViewChild('bannerAnnouncementModal', { static: false }) bannerAnnouncementModalTpl!: TemplateRef<unknown>;
  @ViewChild('quickActionsModalShell', { static: false }) quickActionsModalTpl!: TemplateRef<unknown>;
  @ViewChild('mdSidebar', { static: false }) mdSidebarRef!: ElementRef<HTMLElement>;
  @ViewChild('quickActionBulkApFileInput')
  quickActionBulkApFileInput?: ElementRef<HTMLInputElement>;

  private swMessageHandler: ((event: MessageEvent) => void) | null = null;

  private bannerAnnouncementModalRef: BsModalRef | null = null;
  private quickActionsModalRef: BsModalRef | null = null;
  private readonly globalKeydownListener = (event: Event): void => this.onGlobalKeydown(event);
  /** Announcement shown in the "more" dialog (fixed when opened, does not follow banner slider). */
  bannerModalAnnouncement: AnnouncementListItem | null = null;

  public user: UserModel;

  public role: string;

  activeRoleId: any;

  uniqueRoles: any[] = [];

  permissions: AppPermissions;

  public RoleConstant = RoleConstant;

  public SidebarMenuItem = SidebarMenuItem;

  public serviceRequestType = ServiceRequestType;

  private ngUnSubscribe: Subject<void> = new Subject<void>();

  isLoading$ = new BehaviorSubject<boolean>(false);
  isBulkApUploading$ = new BehaviorSubject<boolean>(false);
  /** Bumped on every refresh — a fresh value each time so distinctUntilChanged never drops a refetch. */
  refetch$ = new BehaviorSubject<number>(0);
  searchCtrl = new FormControl('');
  statusCtrl = new FormControl('');
  pageCtrl = new FormControl(1);
  /** Active tab in bell dropdown: Notifications or Announcements */
  activeNotificationTab: 'notifications' | 'announcements' = 'notifications';
  /** Top banner: today's active announcements (from Announcement API), filtered client-side */
  bannerAnnouncements: AnnouncementListItem[] = [];
  /** Current slide index for banner when multiple announcements */
  currentBannerIndex = 0;
  /** Auto-advance banner every 8 seconds when multiple announcements */
  private readonly bannerAutoSlideSeconds = 8;
  private bannerAutoSlideTimer: ReturnType<typeof setInterval> | null = null;
  /** Fade timing used when switching between multiple banner announcements. */
  private readonly bannerTransitionMs = 360;
  private bannerTransitionTimer: ReturnType<typeof setTimeout> | null = null;
  isBannerTransitioning = false;
  /** Dismissed banner announcement IDs (persisted) so they don't show again */
  private dismissedBannerIds: Set<string> = new Set();

  private readonly sseService = inject(SseService);
  private readonly appRealtimeEventsSvc = inject(AppRealtimeEventsService);
  private readonly userContextRefreshSvc = inject(UserContextRefreshService);
  /** Re-fetches the bell count (role switch); first fetch runs on login. */
  private readonly countRefresh$ = new Subject<void>();
  private readonly sseReconnect$ = new Subject<void>();
  private readonly sseEventsUrl = `${environment.apiUrl}/api/events`;

  /** Real-time events from SSE — reconnects on login and when role changes. */
  events$ = this.sseReconnect$.pipe(
    startWith(undefined),
    switchMap(() =>
      this.sseService.connect<NotificationEvent>(this.sseEventsUrl, { emitOnConnect: true }).pipe(
        filter((event): event is NotificationEvent => event !== 'connected'),
        tap((event) => this.appRealtimeEventsSvc.publishNotificationEvent(event)),
        retry({ delay: 3000 })
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntilDestroyed()
  );

  /**
   * GET self/count right away on login and role switch — SSE takes a while to
   * connect, so this fills the badge until real-time events take over.
   */
  private readonly selfCount$ = this.countRefresh$.pipe(
    startWith(undefined),
    switchMap(() => this.notificationSvc.searchCount()),
    map((count) => this.normalizeCount(count))
  );

  /** Bell badge count — self/count first, then SSE events keep it current. */
  counts$ = merge(
    this.events$.pipe(
      filter((event) => this.appRealtimeEventsSvc.isNotificationCountEvent(event)),
      map((event) => this.normalizeCount(event))
    ),
    this.selfCount$
  ).pipe(
    distinctUntilChanged(),
    startWith(0),
    shareReplay(1)
  );

  quickActionsSearchTerm = '';
  quickActions: Array<{
    id: string;
    label: string;
    route: string;
    shortcut: string;
    iconPath: string;
    moduleKey?: AppModuleKey;
    allowedRoleIds?: readonly string[];
    excludedRoleIds?: readonly string[];
    actionKind?:
    | 'navigate'
    | 'createAnnouncementModal'
    | 'openBulkApRequestsModal'
    | 'bulkApInvoiceUpload';
  }> = [
      {
        id: 'create-accounts-payable',
        label: 'Create Bills',
        route: '/accounts-payable/new',
        shortcut: 'Cmd+Shift+I',
        iconPath: 'assets/sidebar-icons/AP.svg',
        moduleKey: 'ap',
        excludedRoleIds: [
          RoleConstant.directorOperations,
          RoleConstant.supervisor,
          RoleConstant.vendorId,
        ],
      },
      {
        id: 'create-bulk-ap-request',
        label: 'Create Bills in bulk',
        route: '/accounts-payable/list',
        shortcut: 'Cmd+Shift+B',
        iconPath: 'assets/sidebar-icons/AP.svg',
        allowedRoleIds: [RoleConstant.accountsPayableCreator],
        actionKind: 'bulkApInvoiceUpload',
      },
      {
        id: 'create-approval-process',
        label: 'Create Approval Process',
        route: `/approval-processes/create?type=${ServiceRequestType.repairAndMainenance}`,
        shortcut: 'Cmd+Shift+O',
        iconPath: 'assets/sidebar-icons/organization.svg',
        moduleKey: 'approvalProcess'
      },
      {
        id: 'create-expense-request',
        label: 'Create Expense Request',
        route: '/expenses/new',
        shortcut: 'Cmd+Shift+E',
        iconPath: 'assets/sidebar-icons/Expense.svg',
        moduleKey: 'expense'
      },
      {
        id: 'create-rm-request',
        label: 'Create R&M Request',
        route: '/repairs-and-maintenance/new',
        shortcut: 'Cmd+Shift+R',
        iconPath: 'assets/sidebar-icons/R&M.svg',
        moduleKey: 'RM'
      },
      { id: 'create-asset', label: 'Create Asset', route: '/asset-management/new', shortcut: 'Cmd+Shift+A', iconPath: 'assets/sidebar-icons/AssetM.svg', moduleKey: 'asset' },
      { id: 'create-company', label: 'Create Organization', route: '/company/create', shortcut: 'Cmd+Shift+O', iconPath: 'assets/sidebar-icons/Companies.svg', moduleKey: 'company' },
      { id: 'create-location', label: 'Create Location', route: '/store/create', shortcut: 'Cmd+Shift+L', iconPath: 'assets/sidebar-icons/locations.svg', moduleKey: 'store' },
      { id: 'create-user', label: 'Create User', route: '/users/new', shortcut: 'Cmd+Shift+U', iconPath: 'assets/sidebar-icons/users.svg', moduleKey: 'user' },
      { id: 'create-vendor', label: 'Create Vendor', route: '/vendors/create', shortcut: 'Cmd+Shift+Y', iconPath: 'assets/sidebar-icons/vendors.svg', moduleKey: 'vendors' },
      // Use a non-browser-reserved shortcut on Windows. Ctrl+Shift+N opens Incognito in Chromium.
      {
        id: 'create-announcement',
        label: 'Create Announcement',
        route: '/announcements/list?openCreateAnnouncement=1',
        shortcut: this.isMacPlatform ? 'Cmd+Shift+N' : 'Ctrl+Alt+N',
        iconPath: 'assets/sidebar-icons/An-icon.svg',
        allowedRoleIds: ANNOUNCEMENT_ANY_AUTHOR_ROLE_IDS,
        actionKind: 'createAnnouncementModal',
      },
    ];
  private searchTerm$ = this.searchCtrl.valueChanges.pipe(
    startWith(''),
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this.pageCtrl.setValue(1, { emitEvent: false }))
  );
  // private status$ = this.statusCtrl.valueChanges.pipe(
  //   startWith(''),
  //   distinctUntilChanged(),
  //   tap(() => this.pageCtrl.setValue(1, { emitEvent: false }))
  // );
  private page$ = this.pageCtrl.valueChanges.pipe(
    startWith(1),
    distinctUntilChanged()
  );

  /** Coalesces rapid refetch triggers (SSE, push, navigation) into one emission. */
  private readonly debouncedRefetch$ = merge(
    of(this.refetch$.value),
    this.refetch$.pipe(debounceTime(200))
  ).pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /** Single daily/search fetch shared by bell list, unread dot, and top banner. */
  private readonly dailyAnnouncementsFetch$ = this.debouncedRefetch$.pipe(
    switchMap(() => {
      this.announcementsLoading$.next(true);
      return this.fetchDailyAnnouncementsForChrome().pipe(
        finalize(() => this.announcementsLoading$.next(false))
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  notificationlist$ = combineLatest([
    this.searchTerm$,
    this.page$,
    this.debouncedRefetch$,
  ]).pipe(
    debounceTime(100),
    tap(() => {
      this.isLoading$.next(true);
    }),
    switchMap(([search, _page, _reFetch]) => {
      const payload = {
        // type: SearchType.accountsPayable,
        ids: [],
        page: this.pageCtrl.value,
        pageSize: 10,
        keyword: search,
        notificationState: NotificationState.state,
      } as SearchRequest;

      if (!search) delete payload.keyword;
      // if (!status) delete payload.status;

      return this.notificationSvc
        .searchAsync<NotificationsResponse>(payload, 'Notification')
        .pipe(
          tap(() => {
            this.isLoading$.next(false);
          })
        );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /** Announcements for bell dropdown – derived from shared daily/search (first 5). */
  announcementlist$ = this.dailyAnnouncementsFetch$.pipe(
    map((data) => ({
      ...data,
      items: data.items.slice(0, 5),
      pageSize: 5,
    })),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Notifications tab red dot only — from notification count (independent of announcements).
   */
  notificationsTabUnreadDot$ = this.counts$.pipe(
    map((c) => {
      const n = Number(c);
      return !Number.isNaN(n) && n > 0;
    }),
    distinctUntilChanged(),
    shareReplay(1)
  );

  /**
   * Announcements tab red dot only — derived from shared daily/search (no extra pagination).
   */
  announcementsTabUnreadDot$ = this.dailyAnnouncementsFetch$.pipe(
    map((data) =>
      data.items.some((a) => (a.status || '').toLowerCase() === 'unread')
    ),
    distinctUntilChanged(),
    shareReplay(1)
  );

  announcementsLoading$ = new BehaviorSubject<boolean>(false);

  // counts$ = combineLatest([
  //   this.refetch$,
  // ]).pipe(
  //   debounceTime(50),
  //   tap(() => {
  //     this.isLoading$.next(true);
  //   }),
  //   switchMap(([_reFetch]) => {

  //     return this.notificationSvc
  //       .searchCount()
  //       .pipe(
  //         tap(() => {
  //           this.isLoading$.next(false);
  //         })
  //       );
  //   })
  // );

  // counts$ = this.notificationSvc
  //   .searchCount()
  //   .pipe(
  //     tap(() => {
  //       this.isLoading$.next(false);
  //     })
  //   );
  selectedCompany: any;

  /** Header organization picker; reloads when organizations are created, deleted or change status. */
  companies$ = this.organizationSvc.listChanged$.pipe(
    startWith(undefined),
    switchMap(() => this.companySvc.searchAllCompanies({ page: 1, pageSize: 1000 })),
    map((data) => data.items),
    shareReplay(1)
  );

  hideChrome = false;
  mdAiPanelOpen = false;
  private latestUrl = '';
  public currentPageTitle = 'Homepage';

  /**
   * True on the AI-First page, where the Assist rail trigger is hidden.
   *
   * A getter off the router rather than a field updated on navigation: the title is assigned at
   * three separate places in this component and a fourth is easy to add, so any flag maintained
   * alongside it is one missed assignment away from being wrong. This cannot drift.
   */
  public get isAiFirstRoute(): boolean {
    return (this.router.url || '').split('?')[0].includes('/meal-dynamics-ai');
  }
  public isSidebarCollapsed = false;
  public isMobileSidebarOpen = false;
  public isProfileMenuOpen = false;
  public isProfileMenuClosing = false;
  private profileMenuCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly profileMenuTransitionMs = 220;
  public expandedSidebarGroups: {
    home: boolean;
    ap: boolean;
    repair: boolean;
    expense: boolean;
    vendor: boolean;
  } = {
      home: false,
      ap: false,
      repair: false,
      expense: false,
      vendor: false,
    };
  /**
   * Which AP parent is open. Exact BE label — "Account Payable" ≠ "Accounts Payable".
   * Kept across route sync when multiple parents share the same pageUrl.
   */
  public expandedApMenu: string | null = null;
  public openCollapsedSidebarGroup: string | null = null;
  /** Desktop side-flyout coordinates (`position: fixed`). */
  public sidebarFlyoutPosition: { top: string; left: string } | null = null;
  private sidebarFlyoutCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingSidebarFlyoutCloseKey: string | null = null;
  public homepageNavGroup: PageNavGroup | null = null;
  public mdAiNavGroup: PageNavGroup | null = null;
  public apNavGroups: ApNavGroup[] = [];
  public expenseNavGroup: ExpenseNavGroup | null = null;
  public vendorNavGroup: VendorNavGroup | null = null;
  public repairNavGroup: RmNavGroup | null = null;

  constructor(
    private authSvc: AuthService,
    private cd: ChangeDetectorRef,
    private ngZone: NgZone,
    public contextSvc: ContextService,
    public aiPrefSvc: AiPreferenceService,
    private apPermissionSvc: ApPermissionService,
    private expensePermissionSvc: ExpensePermissionService,
    private vendorPermissionSvc: VendorPermissionService,
    private rmPermissionSvc: RmPermissionService,
    private userSvc: UserService,
    private notificationSvc: NotificationsService,
    private announcementSvc: AnnouncementService,
    private companySvc: CompanyService,
    private messagingService: MessagingService,
    private shellChrome: ShellChromeService,
    private router: Router,
    private modalService: BsModalService,
    private dropdownCoordinator: DropdownCoordinatorService,
    private mdAiPanelService: MdAiPanelService,
    private sessionStorageService: SessionStorageService,
    private apBulkUploadSvc: ApBulkInvoiceUploadService,
    public impersonationSvc: ImpersonationService,
    private organizationSvc: OrganizationService
  ) { }

  readonly quickActionBulkApAccept = [
    ...AP_BULK_INVOICE_ALLOWED_EXTENSIONS,
    'application/pdf',
    'image/png',
    'image/jpeg',
  ].join(',');

  /**
   * What the person actually chose, kept while a surface is temporarily forcing the nav collapsed.
   * Null when nothing is forcing it.
   */
  private navCollapsePreference: boolean | null = null;
  private navCollapseSub?: Subscription;

  ngOnInit() {
    this.isSidebarCollapsed = localStorage.getItem('mdShellSidebarCollapsed') === 'true';
    this.loadImpersonatedOrganizationModules();
    this.reloadContextOnOrganizationChange();

    // The AI surfaces ask for a collapsed nav; the design puts AI First at 81px + a 976px column
    // and moves the shell to 81 + 900 + 459 when the Assist rail opens.
    //
    // Their preference is remembered and restored, and deliberately NOT written to localStorage:
    // someone who likes an expanded nav should not find it collapsed for the rest of the session
    // because they once opened the assistant.
    this.navCollapseSub = this.shellChrome.navCollapsed$.subscribe(forced => {
      if (forced) {
        if (this.navCollapsePreference === null) {
          this.navCollapsePreference = this.isSidebarCollapsed;
        }
        this.isSidebarCollapsed = true;
      } else if (this.navCollapsePreference !== null) {
        this.isSidebarCollapsed = this.navCollapsePreference;
        this.navCollapsePreference = null;
      }
      this.markForCheck();
    });
    if (typeof document !== 'undefined') {
      // Use capture so `preventDefault()` runs before browser default shortcuts.
      document.addEventListener('keydown', this.globalKeydownListener, true);
    }

    this.dropdownCoordinator.onOpened$
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe((sourceId) => {
        if (sourceId !== DROPDOWN_COORDINATOR_IDS.profileMenu) {
          this.closeProfileDropdown();
        }
        if (sourceId !== DROPDOWN_COORDINATOR_IDS.notificationDropdown) {
          this.closeNotificationDropdown();
        }
      });
    // if (this.checkIfUserHasRole(RoleConstant.globalAdminId)) {
    //   this.companies$.pipe(take(1)).subscribe(companies => {
    //     if (companies?.length > 0) {
    //       if (this.selectedCompany) {
    //         this.selectedCompany = this.selectedCompany;
    //       } else {
    //         this.selectedCompany = companies[0].id;
    //       }

    //       this.contextSvc.setCompanyId(this.selectedCompany);
    //     }
    //   });
    // }

    // Load last selected company from localStorage
    const savedCompanyId = localStorage.getItem('selectedCompanyId');

    if (this.checkIfUserHasRole(RoleConstant.globalAdminId)) {
      this.companies$.pipe(take(1)).subscribe((companies) => {
        if (companies?.length > 0) {
          if (savedCompanyId) {
            // Always prefer previously selected value
            this.selectedCompany = savedCompanyId;
          } else {
            // First login → pick index 0
            this.selectedCompany = companies[0].id;
          }

          this.contextSvc.setCompanyId(this.selectedCompany);
        }
      });
    } else {
      // Non-admin users: company is derived from their user profile (userRoles[0].company.id).
      // Must be persisted so the HTTP interceptor can send the Company header.
      this.contextSvc.user
        .pipe(filter((u) => !!u), take(1))
        .subscribe((u) => {
          const companyId = u?.userRoles?.[0]?.company?.id;
          if (companyId) {
            this.selectedCompany = companyId;
            this.contextSvc.setCompanyId(companyId);
          }
        });
    }

    // Skip FCM registration + all shell startup API calls (companies,
    // notifications, announcements) on the external new-asset flow AND the
    // public vendor invite/payment-details flow — both render this shell for
    // an anonymous visitor with no logged-in session (AuthGuard/ContextGuard
    // already bypass login for these), so none of that authenticated
    // startup logic should fire — it was causing stray 400s (duplicate-check,
    // daily announcements) on the invite page opened straight from the email.
    const currentUrl = this.router.url ?? '';
    const inviteId = getInviteIdFromUrl(currentUrl);
    const isPublicVendorFlow =
      !this.contextSvc.user.value &&
      !!inviteId &&
      (isVendorCreateRoute(currentUrl) || isVendorPaymentDetailsRoute(currentUrl));
    const isNewAssetFlow = currentUrl.includes('/new-asset') || isPublicVendorFlow;
    if (isNewAssetFlow) {
      // External new-asset flow performs its own authentication bootstrap.
      // Avoid shell-level startup API calls so authenticate happens first.
      this.latestUrl = this.router.url;
      this.currentPageTitle = this.resolvePageTitle(this.latestUrl);
      this.syncExpandedSidebarGroupsWithRoute(this.latestUrl);
      this.updateChromeVisibility();
      return;
    }

    if (!isNewAssetFlow) {
      this.messagingService.requestPermission();
    }

    this.messagingService.currentMessage.subscribe((msg) => {
      if (!msg) return;
      this.ngZone.run(() => {
        this.handlePushMessage(msg, 'foreground');
      });
    });

    // Listen for background push payloads forwarded from `firebase-messaging-sw.js`
    if (typeof navigator !== 'undefined' && (navigator as any).serviceWorker?.addEventListener) {
      this.swMessageHandler = (event: MessageEvent) => {
        const data: any = (event as any)?.data;
        if (data?.id !== 'fcm') return;
        this.ngZone.run(() => {
          this.handlePushMessage(data?.payload, 'service-worker');
        });
      };
      (navigator as any).serviceWorker.addEventListener('message', this.swMessageHandler);
    }

    this.contextSvc.selectedRoleId$.subscribe((roleId) => {
      this.activeRoleId = roleId;
      this.refreshSidebarNavGroups();
    });
    this.contextSvc.multipleUsers
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe((x) => {
        this.user = x;

        const rawRoles = this.user?.userRoles || [];
        this.uniqueRoles = Array.from(
          new Map(rawRoles.map((role) => [role.roleId, role])).values()
        );
        this.refreshSidebarNavGroups();
      });

    this.contextSvc.user.pipe(takeUntil(this.ngUnSubscribe)).subscribe(() => {
      this.updateChromeVisibility();
      this.refreshSidebarNavGroups();
    });
    this.contextSvc.onRoleSwitch$.pipe(takeUntil(this.ngUnSubscribe)).subscribe(() => {
      this.refreshSidebarNavGroups();
    });
    this.contextSvc.onApRolePermissionsChanged$
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe(() => this.refreshSidebarNavGroups());

    const modalSvcAny = this.modalService as {
      onHidden?: Observable<unknown>;
    };
    if (modalSvcAny.onHidden) {
      modalSvcAny.onHidden
        .pipe(takeUntil(this.ngUnSubscribe))
        .subscribe(() => this.scheduleOrphanModalBackdropCleanup());
    }

    this.router.events
      .pipe(
        takeUntil(this.ngUnSubscribe),
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event) => {
        this.latestUrl = event.urlAfterRedirects || event.url;
        this.currentPageTitle = this.resolvePageTitle(this.latestUrl);
        this.isMobileSidebarOpen = false;
        this.clearSidebarFlyout();
        this.removeOrphanModalBackdrops();
        try {
          const lastUrl = sessionStorage.getItem('bajco-last-url');
          if (lastUrl) {
            sessionStorage.setItem('bajco-prev-url', lastUrl);
          }
          sessionStorage.setItem('bajco-last-url', this.latestUrl);
        } catch {
          // no-op
        }
        // Keep sidebar expanded/collapsed groups in sync with the current route.
        // This prevents child menus from staying open after navigating away.
        this.syncExpandedSidebarGroupsWithRoute(this.latestUrl);
        this.updateChromeVisibility();
      });

    this.latestUrl = this.router.url;
    this.currentPageTitle = this.resolvePageTitle(this.latestUrl);
    this.syncExpandedSidebarGroupsWithRoute(this.latestUrl);
    this.updateChromeVisibility();

    this.mdAiPanelService.panelOpen$
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe(open => (this.mdAiPanelOpen = open));
    this.refreshSidebarNavGroups();

    // Load today's active announcements for top banner (with slider if multiple). Skip for global admin.
    this.loadDismissedBannerIds();
    if (isNewAssetFlow) {
      this.bannerAnnouncements = [];
      this.clearBannerAutoSlide();
      this.clearBannerTransitionTimer();
      return;
    }

    this.dailyAnnouncementsFetch$
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe((data) => {
        const items = data.items;
        const today = this.getTodayLocalDateString();
        this.bannerAnnouncements = items.filter(
          (a) =>
            (a.announcementLifecycleStatus || '').toLowerCase() === 'active' &&
            this.isDateToday(a.scheduledDate, today) &&
            !this.dismissedBannerIds.has(a.id)
        );
        this.currentBannerIndex = 0;
        this.isBannerTransitioning = false;
        this.clearBannerTransitionTimer();
        if (this.currentBannerIndex >= this.bannerAnnouncements.length && this.bannerAnnouncements.length > 0) {
          this.currentBannerIndex = this.bannerAnnouncements.length - 1;
        }
        this.clearBannerAutoSlide();
        if (this.bannerAnnouncements.length > 1) {
          this.startBannerAutoSlide();
        }
        this.markForCheck();
      });
  }

  private isExternalNewAssetRoute(): boolean {
    return this.router.url?.toLowerCase().includes('/asset-management/new-asset');
  }

  private getTodayLocalDateString(): string {
    const t = new Date();
    const y = t.getFullYear();
    const m = String(t.getMonth() + 1).padStart(2, '0');
    const d = String(t.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private isDateToday(scheduledDate: string | undefined, today: string): boolean {
    if (!scheduledDate) return false;
    // scheduledDate is a calendar date sent as UTC midnight ("2026-09-30T00:00:00Z"); compare
    // its date part as-is — converting to local time shifts it to the previous day west of UTC.
    const datePart = /^\d{4}-\d{2}-\d{2}/.exec(scheduledDate)?.[0];
    if (datePart) return datePart === today;
    const d = new Date(scheduledDate);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}` === today;
  }

  private loadDismissedBannerIds(): void {
    try {
      const raw = localStorage.getItem('dismissedAnnouncementBannerIds');
      const ids = raw ? (JSON.parse(raw) as string[]) : [];
      this.dismissedBannerIds = new Set(ids);
    } catch {
      this.dismissedBannerIds = new Set();
    }
  }

  private saveDismissedBannerIds(): void {
    localStorage.setItem('dismissedAnnouncementBannerIds', JSON.stringify([...this.dismissedBannerIds]));
  }

  /** Current announcement shown in banner (for slider) */
  get currentBannerAnnouncement(): AnnouncementListItem | null {
    if (!this.bannerAnnouncements.length) return null;
    const i = Math.min(this.currentBannerIndex, this.bannerAnnouncements.length - 1);
    return this.bannerAnnouncements[i] ?? null;
  }

  private clearBannerTransitionTimer(): void {
    if (this.bannerTransitionTimer != null) {
      clearTimeout(this.bannerTransitionTimer);
      this.bannerTransitionTimer = null;
    }
  }

  private goToBannerSlide(index: number): void {
    const total = this.bannerAnnouncements.length;
    if (!total) return;
    const normalized = ((index % total) + total) % total;
    if (normalized === this.currentBannerIndex) return;

    this.clearBannerTransitionTimer();
    this.isBannerTransitioning = true;
    this.markForCheck();

    this.bannerTransitionTimer = setTimeout(() => {
      this.currentBannerIndex = normalized;
      this.isBannerTransitioning = false;
      this.bannerTransitionTimer = null;
      this.markForCheck();
    }, this.bannerTransitionMs);
  }

  nextBannerSlide(): void {
    if (this.bannerAnnouncements.length <= 1) return;
    this.goToBannerSlide(this.currentBannerIndex + 1);
  }

  prevBannerSlide(): void {
    if (this.bannerAnnouncements.length <= 1) return;
    this.goToBannerSlide(this.currentBannerIndex - 1);
  }

  private clearBannerAutoSlide(): void {
    if (this.bannerAutoSlideTimer != null) {
      clearInterval(this.bannerAutoSlideTimer);
      this.bannerAutoSlideTimer = null;
    }
  }

  private startBannerAutoSlide(): void {
    this.clearBannerAutoSlide();
    this.bannerAutoSlideTimer = setInterval(() => {
      this.nextBannerSlide();
    }, this.bannerAutoSlideSeconds * 1000);
  }

  /** Dismiss only the currently visible banner (persists so it won't show again). Other banners remain. */
  dismissCurrentBannerAnnouncement(): void {
    const current = this.currentBannerAnnouncement;
    if (!current || !this.bannerAnnouncements.length) return;

    // Mark only the dismissed announcement as read in backend.
    if ((current.status || '').toLowerCase() === 'unread' && current.id) {
      this.announcementSvc.markSingleRead(current.id).pipe(take(1)).subscribe({
        next: () => {
          this.reFreshNotifications();
          this.markForCheck();
        },
        error: (err) => console.error('Banner announcement mark-as-read failed', err),
      });
    }

    if (current.id) {
      this.dismissedBannerIds.add(current.id);
      this.saveDismissedBannerIds();
    }
    const idx = this.currentBannerIndex;
    this.bannerAnnouncements = this.bannerAnnouncements.filter((_, i) => i !== idx);
    if (this.bannerAnnouncements.length === 0) {
      this.currentBannerIndex = 0;
      this.isBannerTransitioning = false;
      this.clearBannerTransitionTimer();
      this.clearBannerAutoSlide();
    } else {
      this.currentBannerIndex = Math.min(idx, this.bannerAnnouncements.length - 1);
      if (this.bannerAnnouncements.length === 1) {
        this.clearBannerAutoSlide();
      } else {
        this.startBannerAutoSlide();
      }
    }
    this.markForCheck();
  }

  /** Dismiss all announcements in the banner at once (persists so they won't show again). */
  dismissBannerAnnouncement(): void {
    if (!this.bannerAnnouncements.length) return;

    this.bannerAnnouncements.forEach((a) => a.id && this.dismissedBannerIds.add(a.id));
    this.saveDismissedBannerIds();
    this.bannerAnnouncements = [];
    this.currentBannerIndex = 0;
    this.isBannerTransitioning = false;
    this.clearBannerTransitionTimer();
    this.clearBannerAutoSlide();
    this.markForCheck();
  }

  /** Navigate to announcements (Explore Now action). */
  exploreAnnouncement(): void {
    this.router.navigate(['/notifications/list'], { queryParams: { tab: 'announcements' } });
  }

  /** Open full announcement modal (from banner "more" link). Stores current announcement so dialog does not change when banner advances. */
  openBannerAnnouncementModal(): void {
    if (!this.currentBannerAnnouncement || !this.bannerAnnouncementModalTpl) return;
    this.bannerModalAnnouncement = this.currentBannerAnnouncement;
    this.bannerAnnouncementModalRef = this.modalService.show(this.bannerAnnouncementModalTpl, {
      class: 'modal-dialog-centered banner-announcement-modal-dialog',
      ignoreBackdropClick: false,
      keyboard: true,
    });
    this.attachModalBackdropCleanup(this.bannerAnnouncementModalRef);
  }

  /** Open full announcement modal from bell dropdown item click. */
  openAnnouncementDetailModal(announcement: AnnouncementListItem): void {
    if (!announcement || !this.bannerAnnouncementModalTpl) return;
    this.bannerModalAnnouncement = announcement;
    this.bannerAnnouncementModalRef = this.modalService.show(this.bannerAnnouncementModalTpl, {
      class: 'modal-dialog-centered banner-announcement-modal-dialog',
      ignoreBackdropClick: false,
      keyboard: true,
    });
    this.attachModalBackdropCleanup(this.bannerAnnouncementModalRef);
  }

  closeBannerAnnouncementModal(): void {
    const ref = this.bannerAnnouncementModalRef;
    this.bannerAnnouncementModalRef = null;
    this.bannerModalAnnouncement = null;
    ref?.hide();
  }

  get filteredQuickActions() {
    const term = (this.quickActionsSearchTerm || '').toString().trim().toLowerCase();
    const allowedActions = this.effectiveQuickActions.filter((action) => this.canShowQuickAction(action));
    if (!term) return allowedActions;
    return allowedActions.filter((action) => action.label.toLowerCase().includes(term));
  }

  private canShowQuickAction(action: {
    id?: string;
    moduleKey?: AppModuleKey;
    allowedRoleIds?: readonly string[];
    excludedRoleIds?: readonly string[];
  }): boolean {
    const selectedRoleId = this.activeRoleId || localStorage.getItem('selectedRoleId') || '';

    const module = action.id ? QUICK_ACTION_MODULES[action.id] : undefined;
    if (module && !this.contextSvc.isCompanyModuleActive(module)) {
      return false;
    }

    // Inside an organization, Asset Management needs its R&M module.
    if (action.id === 'create-asset' && this.isImpersonating && !this.impersonationSvc.isAssetManagementAvailable) {
      return false;
    }

    if (action.id === 'create-accounts-payable') {
      return this.apPermissionSvc.can(AccountsPayableAction.Create);
    }

    if (action.id === 'create-bulk-ap-request') {
      return this.apPermissionSvc.can(AccountsPayableAction.Create);
    }

    if (action.id === 'create-expense-request') {
      return this.expensePermissionSvc.can(ExpenseAction.Create);
    }

    if (action.excludedRoleIds?.length && selectedRoleId && action.excludedRoleIds.includes(selectedRoleId)) {
      return false;
    }

    if (action.allowedRoleIds?.length) {
      return !!selectedRoleId && action.allowedRoleIds.includes(selectedRoleId);
    }

    if (!action.moduleKey) {
      return true;
    }

    if (action.moduleKey === 'ap') {
      return this.apPermissionSvc.can(AccountsPayableAction.Create);
    }

    if (action.moduleKey === 'expense') {
      return this.expensePermissionSvc.can(ExpenseAction.Create);
    }

    return this.canSelectedRoleCreate(action.moduleKey, selectedRoleId);
  }

  private isPrimaryQuickAction(action: { id?: string }): boolean {
    return action.id === 'create-accounts-payable'
      || action.id === 'create-expense-request'
      || action.id === 'create-rm-request';
  }

  shouldShowQuickActionDivider(
    action: { id?: string },
    index: number,
    actions: Array<{ id?: string }>
  ): boolean {
    if (!this.isPrimaryQuickAction(action)) {
      return false;
    }
    const nextAction = actions[index + 1];
    return !nextAction || !this.isPrimaryQuickAction(nextAction);
  }

  private canSelectedRoleCreate(moduleKey: AppModuleKey, roleId: string): boolean {
    if (!roleId) return false;
    const roleName = ROLE_ID_TO_NAME_MAP[roleId];
    if (!roleName) return false;
    return !!ROLE_PERMISSIONS_MAP[roleName]?.[moduleKey]?.create;
  }

  openQuickActionsModal(template: TemplateRef<unknown>): void {
    this.quickActionsSearchTerm = '';
    this.quickActionsModalRef = this.modalService.show(template, {
      class: 'modal-dialog-centered quick-actions-modal-dialog',
      ignoreBackdropClick: false,
      keyboard: true,
    });
    this.attachModalBackdropCleanup(this.quickActionsModalRef);
  }

  closeQuickActionsModal(): void {
    const ref = this.quickActionsModalRef;
    this.quickActionsModalRef = null;
    ref?.hide();
  }

  onQuickActionSelect(action: {
    route: string;
    actionKind?:
    | 'navigate'
    | 'createAnnouncementModal'
    | 'openBulkApRequestsModal'
    | 'bulkApInvoiceUpload';
  }): void {
    if (action.actionKind === 'createAnnouncementModal') {
      if (this.isCreateAnnouncementModalOpen()) {
        return;
      }

      // Close Quick Actions first, but wait until it's fully hidden to avoid modal stacking/position issues.
      if (this.quickActionsModalRef) {
        const ref = this.quickActionsModalRef;
        this.quickActionsModalRef = null;
        ref.onHidden?.pipe(take(1)).subscribe(() => {
          this.closeAllOpenModals();
          // ngx-bootstrap can leave behind a backdrop/modal-open class briefly;
          // when we open another modal (especially with backdrop disabled) it can cause
          // the background to appear "light/dark" unexpectedly.
          this.removeOrphanModalBackdrops();
          // Small delay to let ngx-bootstrap remove the backdrop/layout
          setTimeout(() => this.navigateToCreateAnnouncementShortcutTarget(action.route), 50);
        });
        ref.hide();
        return;
      }

      // Navigate first; the announcements list page will open the modal based on the query param.
      this.closeAllOpenModals();
      this.removeOrphanModalBackdrops();
      this.navigateToCreateAnnouncementShortcutTarget(action.route);
      return;
    }

    if (action.actionKind === 'bulkApInvoiceUpload') {
      this.closeQuickActionsModal();
      this.closeAllOpenModals();
      this.removeOrphanModalBackdrops();
      // Wait a tick so the quick-actions modal release does not swallow the file dialog.
      setTimeout(() => this.triggerQuickActionBulkApFilePicker(), 50);
      return;
    }

    if (action.actionKind === 'openBulkApRequestsModal') {
      this.closeQuickActionsModal();
      this.closeAllOpenModals();
      this.removeOrphanModalBackdrops();
      this.openBulkApRequestsModal();
      return;
    }

    this.closeQuickActionsModal();
    this.closeAllOpenModals();
    // Ensure any open bootstrap modal/backdrop is cleared before shortcut navigation.
    // This handles cases like announcement create modal being open.
    this.removeOrphanModalBackdrops();
    this.router.navigateByUrl(action.route);
  }

  triggerQuickActionBulkApFilePicker(): void {
    if (!this.apPermissionSvc.can(AccountsPayableAction.Create)) {
      return;
    }
    this.quickActionBulkApFileInput?.nativeElement.click();
  }

  onQuickActionBulkApFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    if (!files.length) {
      return;
    }
    if (!this.apPermissionSvc.can(AccountsPayableAction.Create)) {
      return;
    }
    if (this.isBulkApUploading$.value) {
      return;
    }

    this.isBulkApUploading$.next(true);
    this.apBulkUploadSvc
      .uploadAndCreateDrafts(files)
      .pipe(
        finalize(() => {
          this.isBulkApUploading$.next(false);
          this.cd.markForCheck();
        }),
        takeUntil(this.ngUnSubscribe)
      )
      .subscribe((result) => {
        if (result.succeeded > 0) {
          this.appRealtimeEventsSvc.markBulkDraftUploadPending(
            'AccountsPayable',
            result.succeeded
          );
          void this.router.navigate(['/accounts-payable/list'], {
            queryParams: { draft: '' },
          });
        }
      });
  }

  private isCreateAnnouncementModalOpen(): boolean {
    if (typeof document === 'undefined') return false;
    return !!document.querySelector('.modal.show .create-announcement-dialog');
  }

  private navigateToCreateAnnouncementShortcutTarget(route: string): void {
    const currentPath = (this.router.url || '').split('?')[0].toLowerCase();
    if (currentPath === '/announcements/list') {
      this.router.navigate(['/announcements/list'], {
        queryParams: {
          openCreateAnnouncement: 1,
          quickActionTrigger: Date.now(),
        },
      });
      return;
    }
    this.router.navigateByUrl(route);
  }

  private closeAllOpenModals(): void {
    const modalSvcAny = this.modalService as any;
    const count =
      typeof modalSvcAny?.getModalsCount === 'function'
        ? Number(modalSvcAny.getModalsCount())
        : 0;

    if (Number.isFinite(count) && count > 0) {
      for (let id = count; id >= 1; id--) {
        try {
          this.modalService.hide(id);
        } catch {
          // ignore and continue closing remaining modals
        }
      }
    }

    try {
      this.modalService.hide();
    } catch {
      // ignore
    }

    this.scheduleOrphanModalBackdropCleanup();
  }

  /** Clear ngx-bootstrap backdrops left after modal close (blocks sidebar clicks). */
  private attachModalBackdropCleanup(modalRef: BsModalRef | null | undefined): void {
    modalRef?.onHidden?.pipe(take(1)).subscribe(() => {
      this.scheduleOrphanModalBackdropCleanup();
    });
  }

  private scheduleOrphanModalBackdropCleanup(): void {
    // Backdrop removal can lag behind onHidden by one animation frame.
    setTimeout(() => this.removeOrphanModalBackdrops(), 0);
    setTimeout(() => this.removeOrphanModalBackdrops(), 350);
  }

  private removeOrphanModalBackdrops(): void {
    if (typeof document === 'undefined') return;

    const modalSvcAny = this.modalService as { getModalsCount?: () => number };
    const modalCount =
      typeof modalSvcAny.getModalsCount === 'function'
        ? Number(modalSvcAny.getModalsCount())
        : document.querySelectorAll('.modal.show').length;

    if (modalCount > 0) {
      return;
    }

    document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    document.body?.classList?.remove('modal-open');
    document.body?.style?.removeProperty('overflow');
    document.body?.style?.removeProperty('padding-right');
  }

  getShortcutKeys(shortcut: string): string[] {
    return (shortcut || '')
      .split('+')
      .map((part) => part.trim())
      .filter(Boolean);
  }

  getShortcutIcon(token: string): string {
    const value = (token || '').trim().toLowerCase();
    if (value === 'cmd' || value === 'command' || value === 'ctrl') {
      return 'assets/icons/cmd.svg';
    }
    if (value === 'shift') {
      return 'assets/icons/shift.svg';
    }
    const letter = value.slice(0, 1).toUpperCase();
    return `assets/icons/${letter}.svg`;
  }

  isWideShortcutKey(token: string): boolean {
    const value = (token || '').trim().toLowerCase();
    return value === 'shift';
  }

  getShortcutDisplayLabel(token: string): string {
    const value = (token || '').trim().toLowerCase();
    if (value === 'cmd' || value === 'command' || value === 'ctrl') {
      return '⌘';
    }
    if (value === 'shift') {
      return 'Shift';
    }
    return (token || '').trim().toUpperCase();
  }

  private get isMacPlatform(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  onGlobalKeydown(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (this.isMobileSidebarOpen) {
      if (keyboardEvent.key === 'Escape') {
        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();
        this.closeMobileSidebar();
        return;
      }

      if (keyboardEvent.key === 'Tab') {
        this.trapFocusInMobileSidebar(keyboardEvent);
      }
    }

    // Ctrl/Cmd + K opens quick actions from anywhere.
    if ((keyboardEvent.ctrlKey || keyboardEvent.metaKey) && keyboardEvent.key.toLowerCase() === 'k') {
      keyboardEvent.preventDefault();
      keyboardEvent.stopPropagation();
      if (!this.quickActionsModalRef && this.quickActionsModalTpl) {
        this.openQuickActionsModal(this.quickActionsModalTpl);
      }
      return;
    }

    // Execute quick action shortcuts globally (even when modal is closed).
    if (!(keyboardEvent.ctrlKey || keyboardEvent.metaKey)) {
      return;
    }

    // Shortcuts must work globally regardless of the modal search term.
    const allowedActions = this.effectiveQuickActions.filter((action) => this.canShowQuickAction(action));
    const matchedAction = allowedActions.find((action) =>
      this.matchesQuickActionShortcut(keyboardEvent, action.shortcut)
    );

    if (!matchedAction) {
      return;
    }

    keyboardEvent.preventDefault();
    keyboardEvent.stopPropagation();
    this.onQuickActionSelect(matchedAction);
  }

  private matchesQuickActionShortcut(event: KeyboardEvent, shortcut: string): boolean {
    const normalized = (shortcut || '').replace(/\s+/g, '').toLowerCase();
    if (!normalized) return false;

    const parts = normalized.split('+').filter(Boolean);
    const keyPart = parts[parts.length - 1];

    const needsCtrl = parts.includes('ctrl') || parts.includes('cmd') || parts.includes('command');
    const needsAlt = parts.includes('alt');
    const needsShift = parts.includes('shift');

    const hasCtrl = event.ctrlKey || event.metaKey;
    if (needsCtrl !== hasCtrl) return false;
    if (needsAlt !== event.altKey) return false;
    if (needsShift !== event.shiftKey) return false;

    return event.key.toLowerCase() === keyPart;
  }

  /** Resolve `callToAction` from API: use as-is with protocol, keep leading `/` paths, else prepend https://. */
  private resolveAnnouncementCallToActionUrl(raw: string | null | undefined): string | null {
    const url = (raw ?? '').trim();
    if (!url) return null;
    return /^https?:\/\//i.test(url) ? url : (url.startsWith('/') ? url : `https://${url}`);
  }

  /** `href` for top-banner CTA link (accessibility / open in new tab). */
  get bannerTopCallToActionHref(): string {
    return this.resolveAnnouncementCallToActionUrl(this.currentBannerAnnouncement?.callToAction) ?? '#';
  }

  /** Top banner: open `callToAction` URL in a new tab. */
  onBannerTopCallToActionClick(event: Event): void {
    event.preventDefault();
    const targetUrl = this.resolveAnnouncementCallToActionUrl(this.currentBannerAnnouncement?.callToAction);
    if (!targetUrl) return;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }

  /** Call to Action from banner announcement modal: open callToAction URL from response in new tab (use URL as received; add https:// only when no protocol). */
  onBannerModalCallToAction(): void {
    const targetUrl = this.resolveAnnouncementCallToActionUrl(this.bannerModalAnnouncement?.callToAction);
    this.closeBannerAnnouncementModal();
    if (targetUrl) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
  }

  /** Bell badge text: caps large unread counts at "99+". */
  formatNotificationCount(count: number | null | undefined): string {
    const value = Number(count) || 0;
    return value > 99 ? '99+' : String(value);
  }

  onNotificationIconClick(event?: Event) {
    event?.stopPropagation();
    this.closeProfileDropdown();
    this.dropdownCoordinator.notifyOpened(
      DROPDOWN_COORDINATOR_IDS.notificationDropdown
    );
    this.reFreshNotifications();
  }

  trackByFn(index: number, item: NotificationsResponse) {
    return item.id;
  }

  trackByAnnouncementFn(index: number, item: AnnouncementListItem) {
    return item.id;
  }

  /**
   * User-facing unread: prefer wrapper `status` (read receipt for this row). Nested
   * `dailyAnnouncementMinModel.status` can stay UnRead while wrapper is Read — API quirk.
   */
  private isDailyAnnouncementUnreadForUser(x: any): boolean {
    const a = x?.dailyAnnouncementMinModel ?? x;
    if (!a) return false;
    const raw = x?.status ?? a?.status ?? '';
    return String(raw).toLowerCase() === 'unread';
  }

  /** One daily/search call for bell, unread dot, and banner (pageSize 100). */
  private fetchDailyAnnouncementsForChrome(): Observable<{
    items: AnnouncementListItem[];
    totalRecords: number;
    currentPage: number;
    pageSize: number;
  }> {
    const empty: {
      items: AnnouncementListItem[];
      totalRecords: number;
      currentPage: number;
      pageSize: number;
    } = { items: [], totalRecords: 0, currentPage: 1, pageSize: 100 };
    if (this.isExternalNewAssetRoute() || this.contextSvc.isGlobalAdmin()) {
      return of(empty);
    }
    return this.announcementSvc.dailySearch({ page: 1, pageSize: 100, frequency: 'Daily' }).pipe(
      map((res: any) => {
        const rawItems = res?.items ?? [];
        const items = rawItems.map((x: any) => this.mapDailyAnnouncementItem(x));
        return {
          items,
          totalRecords: res?.totalRecords ?? res?.totalCount ?? items.length,
          currentPage: res?.currentPage ?? 1,
          pageSize: res?.pageSize ?? 100,
        };
      }),
      catchError(() => of(empty))
    );
  }

  /** Map daily/search wrapper (with dailyAnnouncementMinModel) to a flat AnnouncementListItem. Type is string "General" | "Alert". Uses wrapper createdOn for bell panel time. */
  mapDailyAnnouncementItem(x: any): AnnouncementListItem {
    const wrapperId = x?.id;
    const a = x?.dailyAnnouncementMinModel ?? x;
    if (!a) return a;
    const wrapperStatus = x?.status ?? a.status;
    const wrapperCreatedOn = x?.createdOn;
    const rawType = x?.type ?? a?.type;
    const typeStr = this.normalizeAnnouncementType(rawType);
    return {
      ...a,
      id: wrapperId ?? a.id,
      status: wrapperStatus,
      originalAnnouncementId: a.id,
      type: typeStr,
      createdAt: wrapperCreatedOn ?? a?.createdOn ?? a?.createdAt,
    } as AnnouncementListItem;
  }

  /** Normalize type to "General" or "Alert" (API may send string "General" | "Alert" or legacy number). */
  normalizeAnnouncementType(raw: any): string {
    if (raw == null || raw === '') return 'General';
    if (typeof raw === 'number') return raw === 1 ? 'Alert' : 'General';
    const s = String(raw).trim().toLowerCase();
    return s === 'alert' || s === 'alertcritical' ? 'Alert' : 'General';
  }

  /** Type pill class for bell dropdown: General (purple) or Alert (red) */
  getAnnouncementTypeClass(type: string | undefined): string {
    const t = (type || '').toLowerCase();
    if (t === 'alert' || t === 'alertcritical') return 'announcement-type-alert';
    return 'announcement-type-general';
  }

  getAnnouncementTypeLabel(type: string | undefined): string {
    const t = (type || '').toLowerCase();
    if (t === 'alert' || t === 'alertcritical') return 'Alert';
    return 'General';
  }

  /** Top banner pill label: "Announcement" for General, "Alert" for Alert type */
  getBannerPillLabel(): string {
    const t = (this.currentBannerAnnouncement?.type || '').toLowerCase();
    return t === 'alert' || t === 'alertcritical' ? 'Alert' : 'Announcement';
  }

  /** Same as getBannerPillLabel but for a given type (used in modal with stored announcement). */
  getBannerPillLabelForType(type: string | undefined): string {
    const t = (type || '').toLowerCase();
    return t === 'alert' || t === 'alertcritical' ? 'Alert' : 'Announcement';
  }

  /** Banner display text: "title: description" when both present, else title or description. Used for tooltip and length check. */
  get bannerDisplayText(): string {
    const a = this.currentBannerAnnouncement;
    if (!a) return '';
    const title = (a.title ?? '').trim();
    const desc = (a.description ?? '').trim();
    if (title && desc) return `${title}: ${desc}`;
    return title || desc;
  }

  /** True when banner display text is long enough to warrant a "more" link. */
  isBannerTextLong(): boolean {
    return this.bannerDisplayText.length > 100;
  }

  /** Bell dropdown announcement description preview with suffix dots (e.g. "......"). */
  getAnnouncementDescriptionPreview(description: string | undefined, maxChars = 110): string {
    const text = (description || '—').replace(/\s+/g, ' ').trim();
    if (!text) return '—';
    if (text.length <= maxChars) return text;
    return `${text.slice(0, maxChars).trimEnd()}......`;
  }

  getFirstThreeWords(name: string): string {
    if (!name) return '';
    const words = name.split(' ');
    return words.length > 3 ? words.slice(0, 3).join(' ') + '...' : name;
  }

  getUserName(user: any): string {
    const first = user?.firstName?.trim() || '';
    const last = user?.lastName?.trim() || '';
    if (first && last) return `${first} ${last}`;
    if (first) return first;
    if (last) return last;
    return '';
  }

  onCompanyChange(companyId: any) {
    // Inside an organization, switching the picker enters the other organization (keeps the banner right).
    if (this.isImpersonating && companyId?.id) {
      this.impersonationSvc.enter({ id: companyId.id, name: companyId.name });
      return;
    }
    this.selectedCompany = companyId?.id;
    this.contextSvc.setCompanyId(companyId?.id);
    window.location.reload();
  }

  onSwitchRole(role: any): void {
    if (role.roleId === this.activeRoleId) {
      return;
    }

    this.contextSvc.setSelectedRoleId(role.roleId);
    this.contextSvc.notifyRoleSwitch();

    const applyRoleContext = (user: UserModel) => {
      const fullUser = withPreservedUserRoles(
        user,
        this.contextSvc.multipleUsers.value
      );
      const filteredUser = {
        ...fullUser,
        userRoles: fullUser.userRoles.filter((r) => r.roleId === role.roleId),
      };

      this.contextSvc.multipleUsers.next(fullUser);
      this.contextSvc.user.next(filteredUser);
      persistFullUserRolesCache(fullUser);
      this.sessionStorageService.setItem(
        SessionStorageConstants.MULTIPLE_USER,
        fullUser
      );
      this.sessionStorageService.setItem(SessionStorageConstants.USER, filteredUser);

      // Always persist /my companyPermissions for this role (Expense + AP BE-driven).
      this.contextSvc.saveApRoleCompanyPermissions(
        role.roleId,
        user.companyPermissions ?? []
      );

      this.reFreshNotifications();
      this.reconnectSseAndRefreshCount();
      this.router.navigateByUrl('/');
      this.refreshSidebarNavGroups();
      this.apPermissionSvc.refresh();
      this.expensePermissionSvc.refresh();
      this.rmPermissionSvc.refresh();
      this.markForCheck();
    };

    // Every role change → /my so Expense + AP page/action permissions match the role.
    this.userSvc
      .getMyAsync()
      .pipe(take(1))
      .subscribe({
        next: (user) => applyRoleContext(user),
        error: () => {
          const cachedUser = this.contextSvc.multipleUsers.value;
          if (cachedUser) {
            applyRoleContext(cachedUser);
          }
        },
      });
  }

  /**
   * A System Admin changed this organization (modules, syncs, configurations, platform …): BE sends an
   * SSE event and `/me` is reloaded, so the sidebar, Quick Actions and pages use the new setup without
   * a page refresh. Several changes in a row are handled with one reload.
   */
  private reloadContextOnOrganizationChange(): void {
    this.appRealtimeEventsSvc.organizationChanged$
      .pipe(
        filter((event) => {
          const activeCompanyId = this.contextSvc.getActiveCompanyId();
          return !event.companyId || !activeCompanyId || event.companyId === activeCompanyId;
        }),
        debounceTime(500),
        switchMap(() => this.userContextRefreshSvc.refresh().pipe(catchError(() => of(null)))),
        filter((user) => !!user),
        takeUntil(this.ngUnSubscribe)
      )
      .subscribe(() => {
        this.refreshSidebarNavGroups();
        this.apPermissionSvc.refresh();
        this.expensePermissionSvc.refresh();
        this.rmPermissionSvc.refresh();
        this.markForCheck();
      });
  }

  reFreshNotifications() {
    this.refetch$.next(this.refetch$.value + 1);
  }

  /** Role switch: fetch the new role's count immediately and reconnect SSE in parallel. */
  private reconnectSseAndRefreshCount(): void {
    this.countRefresh$.next();
    this.sseReconnect$.next();
  }

  private normalizeCount(value: unknown): number {
    if (value == null) {
      return 0;
    }
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'object' && value !== null && 'count' in value) {
      const n = Number((value as { count: unknown }).count);
      return Number.isNaN(n) ? 0 : n;
    }
    const n = Number(value);
    return Number.isNaN(n) ? 0 : n;
  }

  private handlePushMessage(_msg: any, _source: 'foreground' | 'service-worker'): void {
    try {
      this.reFreshNotifications();
      this.cd.detectChanges();
    } catch (e) {
      this.reFreshNotifications();
      this.cd.detectChanges();
    }
  }

  getModuleRoute(moduleName: string): string {
    const kebab = toKebabCase(moduleName);
    const overrides: Record<string, string> = {
      expense: 'expenses',
      'repair-and-maintenance': 'repairs-and-maintenance',
    };

    return overrides[kebab] ?? kebab;
  }

  /** Close the bell notification dropdown (e.g. before navigating away). */
  closeNotificationDropdown(): void {
    const toggleEl = this.dropdownToggle?.nativeElement;
    if (toggleEl) {
      try {
        const dropdown =
          bootstrap.Dropdown.getInstance(toggleEl) ||
          new bootstrap.Dropdown(toggleEl);
        dropdown.hide();
      } catch {
        // Dropdown may already be closed
      }
    }
  }

  /** Navigate to View all Announcements and close the dropdown. */
  onViewAllAnnouncements(): void {
    this.closeNotificationDropdown();
    this.router.navigate(['/notifications/list'], { queryParams: { tab: 'announcements' } });
  }

  /** Navigate to View all Notifications and close the dropdown. */
  onViewAllNotifications(): void {
    this.closeNotificationDropdown();
    this.router.navigate(['/notifications/list']);
  }

  onNotificationClick(notification: any): void {
    const routeBase = this.getModuleRoute(notification.module);
    const targetUrl = `/${routeBase}/view/${notification.serviceRequestId}`;

    this.closeNotificationDropdown();

    const currentUrl = this.router.url;

    if (currentUrl === targetUrl) {
      // Same route, forceesh refr
      window.location.reload();
      return;
    }

    this.notificationSvc.markSingleRead(notification.id).subscribe({
      next: () => {
        this.router.navigate([
          `/${routeBase}/view`,
          notification.serviceRequestId,
        ]);

        this.reFreshNotifications();
        this.markForCheck();
      },
      error: (err) => {
        console.error('Notification mark‑as‑read failed', err);
        this.router.navigate([
          `/${routeBase}/view`,
          notification.serviceRequestId,
        ]);
      },
    });
  }

  markAllAsRead(): void {
    const pageSize = 100;
    const basePayload = {
      pageSize,
      notificationState: NotificationState.Unread,
    } as SearchRequest;

    const fetchPage = (page: number) =>
      this.notificationSvc.searchAsync<NotificationsResponse>(
        { ...basePayload, page },
        'Notification'
      );

    fetchPage(1)
      .pipe(
        expand((resp) => {
          const nextPage = resp.currentPage + 1;
          const totalPages = Math.ceil(resp.totalRecords / pageSize);
          return nextPage <= totalPages ? fetchPage(nextPage) : EMPTY;
        }),
        map((resp) =>
          (resp.items || [])
            .filter((n) => n.state === 'UnRead')
            .map((n) => n.id)
        ),
        reduce<string[], string[]>((all, ids) => [...all, ...ids], []),
        switchMap((allIds) =>
          allIds.length
            ? this.notificationSvc.markallRead({ ids: allIds } as SearchRequest)
            : of(null)
        )
      )
      .subscribe({
        next: () => this.reFreshNotifications(),
        error: (err) => console.error('Mark‑all‑read failed', err),
      });
  }

  /** Mark single announcement as read in bell dropdown and refresh */
  onAnnouncementClick(announcement: AnnouncementListItem): void {
    this.openAnnouncementDetailModal(announcement);

    if ((announcement.status || '').toLowerCase() !== 'unread') return;
    this.announcementSvc.markSingleRead(announcement.id).pipe(take(1)).subscribe({
      next: () => {
        this.reFreshNotifications();
        this.markForCheck();
      },
      error: (err) => console.error('Announcement mark-as-read failed', err),
    });
  }

  /**
   * ID for POST /v1/Announcement/read — must match single-item read (`onAnnouncementClick`):
   * prefer outer wrapper `id`, else `dailyAnnouncementMinModel.id` (same as `mapDailyAnnouncementItem`).
   */
  private announcementReadIdFromDailyItem(x: any): string | undefined {
    const a = x?.dailyAnnouncementMinModel ?? x;
    if (!a) return undefined;
    const id = x?.id ?? a?.id;
    return id != null && id !== '' ? String(id) : undefined;
  }

  /** Mark all announcements as read across all daily/search records (no frequency restriction). */
  markAllAnnouncementsAsRead(): void {
    const pageSize = 100;
    const fetchPage = (page: number) =>
      this.announcementSvc.dailySearch({
        page,
        pageSize,
      });

    fetchPage(1)
      .pipe(
        expand((resp) => {
          const totalPages = resp?.totalPages ?? Math.ceil((resp?.totalRecords ?? resp?.totalCount ?? 0) / pageSize);
          const nextPage = (resp?.currentPage ?? resp?.page ?? 1) + 1;
          return nextPage <= totalPages ? fetchPage(nextPage) : EMPTY;
        }),
        map((resp: any) => {
          const rawItems = resp?.items ?? [];
          return rawItems
            .filter((x: any) => this.isDailyAnnouncementUnreadForUser(x))
            .map((x: any) => this.announcementReadIdFromDailyItem(x))
            .filter((id: any) => id);
        }),
        reduce<string[], string[]>((all, ids) => [...all, ...ids], []),
        switchMap((allIds) =>
          allIds.length ? this.announcementSvc.markAllRead(allIds).pipe(take(1)) : of(null)
        )
      )
      .subscribe({
        next: () => {
          this.reFreshNotifications();
          this.markForCheck();
        },
        error: (err) => console.error('Mark all announcements read failed', err),
      });
  }

  toggleMdAiPanel(): void {
    this.mdAiPanelService.togglePanel();
  }

  public checkIfUserHasRole(roleId: string): boolean {
    const selectedRoleId = localStorage.getItem('selectedRoleId');
    return selectedRoleId === roleId;
  }

  canShowAnnouncementsMenu(): boolean {
    const selectedRoleId = localStorage.getItem('selectedRoleId');
    return !!selectedRoleId && ANNOUNCEMENT_ANY_AUTHOR_ROLE_IDS.includes(selectedRoleId);
  }

  canAccessCollatedPayment(): boolean {
    return this.apPermissionSvc.canAccessCollatedPayment();
  }

  /**
   * Sidebar item visibility. BE pagePermissions grant a URL when listed; if the
   * URL is omitted (or pagePermissions are missing), legacyAllowed applies.
   */
  canShowSidebarPageUrl(pageUrl: string, legacyAllowed: boolean): boolean {
    return this.contextSvc.canShowSidebarPageUrl(pageUrl, legacyAllowed);
  }

  // ── System Admin portal / Enter Organization ──────────────────────────────

  get isSystemAdmin(): boolean {
    return this.checkIfUserHasRole(RoleConstant.globalAdminId);
  }

  readonly organizationSettingsUrl = ORGANIZATION_SETTINGS_URL;

  /** Organization Settings: shown when /my grants the OrganizationSettings page and Organization View. */
  get canOpenOrganizationSettings(): boolean {
    return this.contextSvc.canOpenOrganizationSettings();
  }

  /** Integration data & import jobs: shown when /my grants the page and at least one of its tabs. */
  get canOpenIntegrationData(): boolean {
    return this.contextSvc.canOpenIntegrationData();
  }

  /** AC 7.1: a System Admin who entered an organization. */
  get isImpersonating(): boolean {
    return this.isSystemAdmin && this.impersonationSvc.isActive;
  }

  /**
   * Which sidebar items a System Admin gets: Homepage, Organizations and Users in the portal, and
   * only organization-level areas while inside an organization (AC 7.3, 7.4). Other roles are not
   * affected; their items keep their own permission checks.
   */
  navAllowed(item: 'mdAi' | 'ap' | 'repair' | 'expense' | 'asset' | 'organizations' | 'companies' | 'locations' | 'users' | 'roles' | 'vendors'): boolean {
    // Modules switched off for the organization are hidden for everyone.
    const module = NAV_MODULES[item];
    if (module && !this.contextSvc.isCompanyModuleActive(module)) return false;
    if (!this.isSystemAdmin) return true;
    if (this.isImpersonating) {
      if (item === 'asset') return this.impersonationSvc.isAssetManagementAvailable;
      return ['asset', 'companies', 'locations', 'users', 'roles', 'vendors'].includes(item);
    }
    return item === 'organizations' || item === 'users';
  }

  /** Inside an organization: its modules decide what the System Admin sees (e.g. Asset Management needs R&M). */
  private loadImpersonatedOrganizationModules(): void {
    const organization = this.isImpersonating ? this.impersonationSvc.organization : null;
    if (!organization) return;
    this.organizationSvc
      .getByIdAsync(organization.id)
      .pipe(take(1))
      .subscribe({
        next: (detail) => {
          this.impersonationSvc.setActiveModules(detail.modules.filter((m) => m.isActive).map((m) => m.key));
          this.cd.markForCheck();
        },
        // Unknown modules leave everything as it was.
        error: () => undefined,
      });
  }

  /** AC 7.2 */
  exitToPlatform(): void {
    this.impersonationSvc.exit();
  }

  /** AC 7.5: inside an organization only Create Company/Location/User/Vendor; "Create Company" means a store group there. */
  private get effectiveQuickActions(): AuthLayout['quickActions'] {
    if (this.isSystemAdmin && !this.isImpersonating) {
      return this.quickActions.filter((a) => PLATFORM_QUICK_ACTION_IDS.includes(a.id));
    }
    if (!this.isImpersonating) return this.quickActions;
    // Built once: a fresh array/objects on every change detection makes *ngFor re-create the buttons,
    // so a click on the remapped "Create Company" item never lands.
    if (!this.impersonationQuickActions) {
      // In the design's order: Company, Location, User, Vendor, Asset.
      this.impersonationQuickActions = IMPERSONATION_QUICK_ACTION_IDS.map((id) => this.quickActions.find((a) => a.id === id))
        .filter((a): a is AuthLayout['quickActions'][number] => !!a)
        .map((a) =>
          a.id === 'create-company'
            ? { ...a, label: 'Create Company', route: '/store-groups/create', iconPath: 'assets/sidebar-icons/Companies.svg', moduleKey: undefined }
            : a
        );
    }
    return this.impersonationQuickActions;
  }

  private impersonationQuickActions: AuthLayout['quickActions'] | null = null;

  canShowAssetManagementNav(): boolean {
    const legacy =
      this.checkIfUserHasRole(RoleConstant.globalAdminId) ||
      this.checkIfUserHasRole(RoleConstant.companyAdminId) ||
      this.checkIfUserHasRole(RoleConstant.srDirectorOperations) ||
      this.checkIfUserHasRole(RoleConstant.directorOperations) ||
      this.checkIfUserHasRole(RoleConstant.supervisor) ||
      this.checkIfUserHasRole(RoleConstant.generalManager) ||
      this.checkIfUserHasRole(RoleConstant.vpOperations) ||
      this.checkIfUserHasRole(RoleConstant.employeeId);

    return this.canShowSidebarPageUrl('asset-management/list', legacy);
  }

  canShowRepairNav(): boolean {
    const roleId = this.contextSvc.selectedRoleId$.value;
    const stored = this.contextSvc.getStoredPermissionsForRole(roleId);
    const companyId = this.contextSvc.getCompanyId();

    if (hasRepairSidebar(stored, companyId)) {
      return true;
    }

    const legacy =
      this.checkIfUserHasRole(RoleConstant.globalAdminId) ||
      this.checkIfUserHasRole(RoleConstant.companyAdminId) ||
      this.checkIfUserHasRole(RoleConstant.srDirectorOperations) ||
      this.checkIfUserHasRole(RoleConstant.directorOperations) ||
      this.checkIfUserHasRole(RoleConstant.supervisor) ||
      this.checkIfUserHasRole(RoleConstant.generalManager) ||
      this.checkIfUserHasRole(RoleConstant.vpOperations) ||
      this.checkIfUserHasRole(RoleConstant.employeeId) ||
      this.checkIfUserHasRole(RoleConstant.vendorId);

    return this.canShowSidebarPageUrl('repairs-and-maintenance/list', legacy);
  }

  canShowApprovalProcessNav(): boolean {
    const legacy =
      this.checkIfUserHasRole(RoleConstant.globalAdminId) ||
      this.checkIfUserHasRole(RoleConstant.companyAdminId) ||
      this.checkIfUserHasRole(RoleConstant.srDirectorOperations) ||
      this.checkIfUserHasRole(RoleConstant.directorOperations) ||
      this.checkIfUserHasRole(RoleConstant.supervisor) ||
      this.checkIfUserHasRole(RoleConstant.generalManager) ||
      this.checkIfUserHasRole(RoleConstant.vpOperations);

    return this.canShowSidebarPageUrl('approval-processes/list', legacy);
  }

  canShowOrganizationsNav(): boolean {
    const legacy =
      this.checkIfUserHasRole(RoleConstant.globalAdminId) ||
      this.checkIfUserHasRole(RoleConstant.companyAdminId) ||
      this.checkIfUserHasRole(RoleConstant.srDirectorOperations) ||
      this.checkIfUserHasRole(RoleConstant.directorOperations) ||
      this.checkIfUserHasRole(RoleConstant.supervisor) ||
      this.checkIfUserHasRole(RoleConstant.generalManager) ||
      this.checkIfUserHasRole(RoleConstant.vpOperations);

    // The System Admin always manages organizations. Other roles follow BE pagePermissions only; the role list
    // applies only when /my sends none.
    if (this.contextSvc.isSystemAdminRole()) return true;
    return this.contextSvc.canShowBePageUrl('companies/list', legacy);
  }

  canShowCompaniesNav(): boolean {
    const legacy =
      this.checkIfUserHasRole(RoleConstant.globalAdminId) ||
      this.checkIfUserHasRole(RoleConstant.companyAdminId) ||
      this.checkIfUserHasRole(RoleConstant.vpOperations) ||
      this.checkIfUserHasRole(RoleConstant.srDirectorOperations) ||
      this.checkIfUserHasRole(RoleConstant.directorOperations) ||
      this.checkIfUserHasRole(RoleConstant.supervisor) ||
      this.checkIfUserHasRole(RoleConstant.generalManager);

    return this.canShowSidebarPageUrl('store-groups/list', legacy);
  }

  canShowLocationsNav(): boolean {
    const legacy =
      this.checkIfUserHasRole(RoleConstant.globalAdminId) ||
      this.checkIfUserHasRole(RoleConstant.companyAdminId) ||
      this.checkIfUserHasRole(RoleConstant.srDirectorOperations) ||
      this.checkIfUserHasRole(RoleConstant.directorOperations) ||
      this.checkIfUserHasRole(RoleConstant.supervisor) ||
      this.checkIfUserHasRole(RoleConstant.generalManager) ||
      this.checkIfUserHasRole(RoleConstant.vpOperations);

    return this.canShowSidebarPageUrl('stores/list', legacy);
  }

  canShowUsersNav(): boolean {
    const legacy =
      this.checkIfUserHasRole(RoleConstant.globalAdminId) ||
      this.checkIfUserHasRole(RoleConstant.companyAdminId) ||
      this.checkIfUserHasRole(RoleConstant.srDirectorOperations) ||
      this.checkIfUserHasRole(RoleConstant.directorOperations) ||
      this.checkIfUserHasRole(RoleConstant.supervisor) ||
      this.checkIfUserHasRole(RoleConstant.generalManager) ||
      this.checkIfUserHasRole(RoleConstant.vpOperations);

    return this.canShowSidebarPageUrl('users/list', legacy);
  }

  /** Roles (Figma "Roles & Permissions"): until BE sends roles/list, the same roles as Users. */
  canShowRolesNav(): boolean {
    const legacy =
      this.checkIfUserHasRole(RoleConstant.globalAdminId) ||
      this.checkIfUserHasRole(RoleConstant.companyAdminId) ||
      this.checkIfUserHasRole(RoleConstant.srDirectorOperations) ||
      this.checkIfUserHasRole(RoleConstant.directorOperations) ||
      this.checkIfUserHasRole(RoleConstant.supervisor) ||
      this.checkIfUserHasRole(RoleConstant.generalManager) ||
      this.checkIfUserHasRole(RoleConstant.vpOperations);

    return this.canShowSidebarPageUrl(ROLES_PAGE_URL, legacy);
  }

  canShowVendorsNav(): boolean {
    const legacy =
      this.checkIfUserHasRole(RoleConstant.globalAdminId) ||
      this.checkIfUserHasRole(RoleConstant.companyAdminId) ||
      this.checkIfUserHasRole(RoleConstant.srDirectorOperations) ||
      this.checkIfUserHasRole(RoleConstant.directorOperations) ||
      this.checkIfUserHasRole(RoleConstant.supervisor) ||
      this.checkIfUserHasRole(RoleConstant.generalManager) ||
      this.checkIfUserHasRole(RoleConstant.accountsPayableManager1) ||
      this.checkIfUserHasRole(RoleConstant.accountsPayableManager2) ||
      this.checkIfUserHasRole(RoleConstant.vpOperations);

    return this.canShowSidebarPageUrl('vendors/list', legacy);
  }

  refreshSidebarNavGroups(): void {
    this.homepageNavGroup = this.contextSvc.getHomepageNavGroup();
    this.mdAiNavGroup = this.aiPrefSvc.allowed ? this.contextSvc.getMealDynamicsAiNavGroup() : null;
    this.apNavGroups = this.apPermissionSvc.getNavGroups();
    this.expenseNavGroup = this.expensePermissionSvc.getNavGroup();
    this.vendorNavGroup = this.vendorPermissionSvc.getNavGroup();
    this.repairNavGroup = getRepairNavGroup(
      this.contextSvc.getStoredPermissionsForRole(
        this.contextSvc.selectedRoleId$.value
      ),
      this.contextSvc.getCompanyId(),
      this.contextSvc.selectedRoleId$.value
    );

    // Re-resolve which AP group is expanded after permissions/nav load.
    if (!this.isSidebarCollapsed) {
      this.syncExpandedSidebarGroupsWithRoute(
        this.latestUrl || this.router.url || ''
      );
    }

    this.markForCheck();
  }

  getHomeSubMenuRoute(pageUrl: string): string | null {
    const normalized = normalizePageUrl(pageUrl);
    if (normalized.startsWith('module=')) {
      return '/';
    }

    return toRouterPath(pageUrl);
  }

  getHomeSubMenuQueryParams(pageUrl: string): Record<string, string> | null {
    const normalized = normalizePageUrl(pageUrl);
    if (normalized.startsWith('module=')) {
      return { module: normalized.slice('module='.length) };
    }

    return null;
  }

  isHomeSubMenuActive(pageUrl: string): boolean {
    const normalized = normalizePageUrl(pageUrl);
    if (normalized.startsWith('module=')) {
      const module = normalized.slice('module='.length);
      return this.isHomeTabActive(module as DashboardViewKey);
    }

    const normalizedCurrentPath = normalizeRoutePath(
      (this.latestUrl || this.router.url || '').split('?')[0]
    );

    return (
      normalizedCurrentPath === normalized ||
      normalizedCurrentPath.startsWith(`${normalized}/`)
    );
  }

  onHomeSubMenuClick(event: Event, pageUrl: string): void {
    this.clearSidebarFlyout();
    this.markForCheck();

    const route = this.getHomeSubMenuRoute(pageUrl);
    if (!route) {
      return;
    }

    this.onSidebarNavLinkClick(
      event,
      [route],
      this.getHomeSubMenuQueryParams(pageUrl) ?? undefined
    );
  }

  getMdAiSubMenuRoute(pageUrl: string): string {
    return toRouterPath(pageUrl);
  }

  isMdAiSubMenuActive(pageUrl: string): boolean {
    const normalizedPageUrl = normalizePageUrl(pageUrl);
    const normalizedCurrentPath = normalizeRoutePath(this.getCurrentRoutePath());

    return (
      normalizedCurrentPath === normalizedPageUrl ||
      normalizedCurrentPath.startsWith(`${normalizedPageUrl}/`)
    );
  }

  onMdAiSubMenuClick(event: Event, pageUrl: string): void {
    this.onSidebarNavLinkClick(event, [this.getMdAiSubMenuRoute(pageUrl)]);
  }

  getExpenseSubMenuRoute(pageUrl: string): string {
    return toRouterPath(pageUrl);
  }

  getExpenseSubMenuQueryParams(pageUrl: string): Record<string, string> | null {
    return getPageUrlQueryParams(pageUrl);
  }

  isExpenseSubMenuActive(pageUrl: string): boolean {
    return isExpenseSubMenuRouteActive(
      pageUrl,
      this.getCurrentRoutePath(),
      this.getCurrentRouteQuery()
    );
  }

  onExpenseSubMenuClick(event: Event, pageUrl: string): void {
    this.clearSidebarFlyout();
    this.markForCheck();
    this.onSidebarNavLinkClick(
      event,
      [this.getExpenseSubMenuRoute(pageUrl)],
      this.getExpenseSubMenuQueryParams(pageUrl) ?? {}
    );
  }

  getVendorSubMenuRoute(pageUrl: string): string {
    return toRouterPath(pageUrl);
  }

  getVendorSubMenuQueryParams(pageUrl: string): Record<string, string> | null {
    return getPageUrlQueryParams(pageUrl);
  }

  isVendorSubMenuActive(pageUrl: string): boolean {
    return isVendorSubMenuRouteActive(
      pageUrl,
      this.getCurrentRoutePath(),
      this.getCurrentRouteQuery()
    );
  }

  onVendorSubMenuClick(event: Event, pageUrl: string): void {
    this.clearSidebarFlyout();
    this.markForCheck();
    this.onSidebarNavLinkClick(
      event,
      [this.getVendorSubMenuRoute(pageUrl)],
      this.getVendorSubMenuQueryParams(pageUrl) ?? {}
    );
  }

  getRepairSubMenuRoute(pageUrl: string): string {
    return toRouterPath(pageUrl);
  }

  getRepairSubMenuQueryParams(pageUrl: string): Record<string, string> | null {
    return getPageUrlQueryParams(pageUrl);
  }

  isRepairSubMenuActive(pageUrl: string): boolean {
    return isRmSubMenuRouteActive(
      pageUrl,
      this.getCurrentRoutePath(),
      this.getCurrentRouteQuery()
    );
  }

  onRepairSubMenuClick(event: Event, pageUrl: string): void {
    this.clearSidebarFlyout();
    this.markForCheck();
    this.onSidebarNavLinkClick(
      event,
      [this.getRepairSubMenuRoute(pageUrl)],
      this.getRepairSubMenuQueryParams(pageUrl) ?? {}
    );
  }

  getApSubMenuRoute(pageUrl: string): string | null {
    if (!isApSubMenuRoute(pageUrl)) {
      return null;
    }

    return toRouterPath(pageUrl);
  }

  getApSubMenuQueryParams(pageUrl: string): Record<string, string> | null {
    return getPageUrlQueryParams(pageUrl);
  }

  isApSubMenuActive(pageUrl: string): boolean {
    return isApSubMenuRouteActive(
      pageUrl,
      this.getCurrentRoutePath(),
      this.getCurrentRouteQuery()
    );
  }

  onApSubMenuItemClick(
    event: Event,
    pageUrl: string,
    apGroup?: ApNavGroup | null
  ): void {
    // Close side flyout immediately on selection (before navigate/route sync).
    this.clearSidebarFlyout();
    this.markForCheck();

    if (apGroup) {
      this.selectApNavGroup(apGroup);
    }

    if (isApSubMenuAction(pageUrl)) {
      event.preventDefault();
      event.stopPropagation();
      this.openBulkApRequestsModal();
      this.closeMenuOnClick(event);
      return;
    }

    this.onApSubMenuClick(event, pageUrl);
  }

  onApSubMenuClick(event: Event, pageUrl: string): void {
    const route = this.getApSubMenuRoute(pageUrl);
    if (!route) {
      return;
    }

    this.onSidebarNavLinkClick(
      event,
      [route],
      this.getApSubMenuQueryParams(pageUrl) ?? {}
    );
  }

  getRoleName(roleID: string): string {
    switch (roleID) {
      case RoleConstant.globalAdminId:
        return 'Global Admin';
      case RoleConstant.companyAdminId:
        return 'Company Admin';
      case RoleConstant.srDirectorOperations:
        return 'Sr Director Operations';
      case RoleConstant.directorOperations:
        return 'Director Operations';
      case RoleConstant.supervisor:
        return 'Supervisor';
      case RoleConstant.generalManager:
        return 'General Manager';
      case RoleConstant.employeeId:
        return 'Employee';
      case RoleConstant.vendorId:
        return 'Vendor';
      case RoleConstant.accountsPayableManager1:
        return 'AP Manager 1';
      case RoleConstant.accountsPayableManager2:
        return 'AP Manager 2';
      case RoleConstant.accountsPayableCreator:
        return 'Accounts Payable Creator';

      case RoleConstant.auditor1:
        return 'Auditor 1';
      case RoleConstant.auditor2:
        return 'Auditor 2';
      case RoleConstant.expenseInitiator:
        return 'Expense Initiator';
      case RoleConstant.vpOperations:
        return 'VP Operations';
      case RoleConstant.RMmanager:
        return 'RM Manager';
      default:
        return 'Unknown Role';
    }
  }

  public logout(): void {
    this.impersonationSvc.clear();
    if (this.profileMenuCloseTimer) {
      clearTimeout(this.profileMenuCloseTimer);
      this.profileMenuCloseTimer = null;
    }
    this.isProfileMenuOpen = false;
    this.isProfileMenuClosing = false;
    this.clearSidebarFlyout();
    this.removeOrphanModalBackdrops();
    this.markForCheck();
    this.authSvc.logout().subscribe(() => { });
  }

  /** Same dialog as Profile page's "Change password" — opened here instead of navigating away. */
  openChangePasswordModal(): void {
    this.modalService.show(ChangePasswordModalComponent, {
      class: 'modal-dialog-centered change-password-modal',
      ignoreBackdropClick: true,
      keyboard: false,
    });
  }

  private markForCheck(): void {
    this.cd.markForCheck();
  }

  private updateChromeVisibility(): void {
    const currentUrl = this.latestUrl || this.router.url;
    const shouldHide = this.shouldHideChrome(currentUrl);

    if (this.hideChrome !== shouldHide) {
      this.hideChrome = shouldHide;
      this.markForCheck();
    }
  }

  private resolvePageTitle(url: string): string {
    const pathname = (url || '').split('?')[0].toLowerCase().replace(/\/$/, '') || '/';
    if (!pathname || pathname === '/') return 'Homepage';

    const dynamicApTitle = this.contextSvc.resolveAccountsPayablePageTitle(
      pathname,
      this.getCurrentRouteQuery()
    );
    if (dynamicApTitle) {
      return dynamicApTitle;
    }

    const exactListTitles: Record<string, string> = {
      // The AI shell names itself. It was falling through to 'Homepage', which is what the header
      // read while the page's own hero said "Meal Dynamics AI" directly beneath it.
      '/meal-dynamics-ai': 'Meal Dynamics AI',
      '/accounts-payable/list': 'Accounts Payable List',
      '/accounts-payable': 'Accounts Payable List',
      '/accounts-payable/vendor-payment-list': 'Vendor Payment List',
      '/accounts-payable/collated-payment': 'Collated Payments',
      '/repairs-and-maintenance/list': 'Repair & Maintenance List',
      '/approval-processes/list': 'Approval Process',
      '/approval-process/list': 'Approval Process',
      '/expenses/list': 'Expense Management',
      '/asset-management/list': 'Asset Management',
      '/companies/list': 'Organizations',
      '/store-groups/list': 'Companies',
      '/stores/list': 'Locations',
      '/users/list': 'Users',
      '/roles/list': 'Roles',
      '/vendors/list': 'Vendors',
      '/announcements/list': 'Announcements',
      '/notifications/list': 'Notifications',
      '/support/list': 'Help & Support',
    };

    if (exactListTitles[pathname]) {
      return exactListTitles[pathname];
    }

    const titleMap: Array<{ match: string; title: string }> = [
      { match: '/notifications/preferences', title: 'Notifications Preferences' },
      { match: '/accounts-payable', title: 'Accounts Payable' },
      { match: '/repairs-and-maintenance', title: 'Repair & Maintenance' },
      { match: '/expenses', title: 'Expense Management' },
      { match: '/asset-management', title: 'Asset Management' },
      { match: '/companies/settings', title: 'Organization Settings' },
      { match: '/store-groups', title: 'Companies' },
      { match: '/store-group', title: 'Companies' },
      { match: '/companies', title: 'Organizations' },
      { match: '/company', title: 'Organizations' },
      { match: '/stores', title: 'Locations' },
      { match: '/store', title: 'Locations' },
      { match: '/users', title: 'Users' },
      { match: '/roles', title: 'Roles' },
      { match: '/vendors', title: 'Vendors' },
      { match: '/vendor', title: 'Vendors' },
      { match: '/announcements', title: 'Announcements' },
      { match: '/notifications', title: 'Notifications' },
      { match: '/ai-preferences', title: 'AI Preferences' },
      { match: '/integration-data', title: 'Integration data & import jobs' },
      { match: '/support', title: 'Help & Support' },
      { match: '/approval-process', title: 'Approval Process' },
      { match: '/approval-processes', title: 'Approval Process' },
      { match: '/profile', title: 'Profile' },
    ];

    const matched = titleMap.find((x) => pathname.startsWith(x.match));
    if (matched?.match === '/accounts-payable' && this.apNavGroups.length) {
      const activeGroup =
        this.resolveApNavGroupForRoute() ?? this.apNavGroups[0];
      return activeGroup?.menu ?? this.apNavGroups[0].menu;
    }

    return matched?.title ?? 'Homepage';
  }

  private shouldHideChrome(url: string): boolean {
    if (!url) {
      return false;
    }

    const normalizedUrl = url.toLowerCase();

    // External asset request form should not render the chrome
    if (normalizedUrl.includes('/asset-management/new-asset')) {
      return true;
    }

    // AP create/edit payable request — full-width without sidebar/header chrome
    if (this.isAccountsPayableFormRoute(normalizedUrl)) {
      return true;
    }

    // Expense create/edit — same full-page chrome-less layout as AP form
    if (this.isExpenseFormRoute(normalizedUrl)) {
      return true;
    }

    // Repair create/edit — same full-page chrome-less layout as expense form
    if (this.isRepairFormRoute(normalizedUrl)) {
      return true;
    }

    // Vendor create/edit — same full-page chrome-less layout as expense form
    if (this.isVendorFormRoute(normalizedUrl)) {
      return true;
    }

    // Create Organization — same full-page chrome-less layout as expense form
    if (this.isOrganizationFormRoute(normalizedUrl)) {
      return true;
    }

    if (normalizedUrl.includes('/vendor/payment-details') || normalizedUrl.includes('/vendors/payment-details')) {
      return true;
    }

    // Make Bulk Payment (from Pending payment Pay in bulk / Pay now)
    if (
      normalizedUrl.includes('/accounts-payable/collated-payment') &&
      this.isMakeBulkPaymentFromList()
    ) {
      return true;
    }

    const hasUser = !!this.contextSvc.user.value;
    if (hasUser) {
      return false;
    }

    const [path, queryString = ''] = url.split('?');
    if (!queryString) {
      return false;
    }

    const params = new URLSearchParams(queryString);
    const inviteId = params.get('InviteId') ?? params.get('inviteId');

    if (!inviteId) {
      return false;
    }

    const normalizedPath = (path || '').toLowerCase();
    const isVendorCreateRoute =
      normalizedPath.includes('/vendors/create') ||
      normalizedPath.includes('/vendor/create');
    const isVendorPaymentDetailsRoute =
      normalizedPath.includes('/vendors/payment-details') ||
      normalizedPath.includes('/vendor/payment-details');

    return isVendorCreateRoute || isVendorPaymentDetailsRoute;
  }

  private isExternalAssetFlow(url: string): boolean {
    return (url || '').toLowerCase().includes('/asset-management/new-asset');
  }

  private isAccountsPayableFormRoute(url: string): boolean {
    if (!url.includes('/accounts-payable/')) {
      return false;
    }

    return (
      url.includes('/accounts-payable/new') ||
      /\/accounts-payable\/details\/[^/?#]+/.test(url)
    );
  }

  private isExpenseFormRoute(url: string): boolean {
    if (!url.includes('/expenses/')) {
      return false;
    }

    return (
      url.includes('/expenses/new') ||
      /\/expenses\/edit\/[^/?#]+/.test(url)
    );
  }

  private isRepairFormRoute(url: string): boolean {
    if (!url.includes('/repairs-and-maintenance/')) {
      return false;
    }

    return (
      url.includes('/repairs-and-maintenance/new') ||
      /\/repairs-and-maintenance\/edit\/[^/?#]+/.test(url)
    );
  }

  private isOrganizationFormRoute(url: string): boolean {
    return url.includes('/companies/create') || url.includes('/company/create');
  }

  private isVendorFormRoute(url: string): boolean {
    return (
      url.includes('/vendors/create') ||
      url.includes('/vendor/create') ||
      /\/vendors\/[^/?#]+\/edit/.test(url)
    );
  }

  private isMakeBulkPaymentFromList(): boolean {
    try {
      const navState =
        this.router.lastSuccessfulNavigation?.extras?.state ?? history.state;
      return Boolean(navState?.['bulkPaymentFromList']);
    } catch {
      return false;
    }
  }

  openBulkApRequestsModal(): void {
    if (!this.apPermissionSvc.can(AccountsPayableAction.Create)) {
      return;
    }

    const modalRef: BsModalRef = this.modalService.show(
      BulkApRequestsModalComponent,
      {
        class: 'modal-dialog-centered modal-lg',
        ignoreBackdropClick: false,
      }
    );
    this.attachModalBackdropCleanup(modalRef);
  }

  toggleSidebarCollapse(): void {
    const willCollapse = !this.isSidebarCollapsed;
    this.clearSidebarFlyout();

    if (willCollapse) {
      this.expandedSidebarGroups = {
        home: false,
        ap: false,
        repair: false,
        expense: false,
        vendor: false,
      };
      this.expandedApMenu = null;
    }

    this.isSidebarCollapsed = willCollapse;
    localStorage.setItem('mdShellSidebarCollapsed', String(this.isSidebarCollapsed));

    if (!this.isSidebarCollapsed) {
      this.syncExpandedSidebarGroupsWithRoute(this.router.url);
    }

    this.markForCheck();
  }

  onSidebarGroupParentClick(
    event: Event,
    group: 'home' | 'ap' | 'repair' | 'expense' | 'vendor'
  ): void {
    if (group === 'ap') {
      this.onApNavGroupParentClick(event, this.apNavGroups[0] ?? null);
      return;
    }

    if (this.isSidebarCollapsed) {
      event.preventDefault();
      event.stopPropagation();
      const opening = this.openCollapsedSidebarGroup !== group;
      if (opening) {
        this.openCollapsedSidebarGroup = group;
        this.updateSidebarFlyoutPosition(event.currentTarget ?? null);
        this.navigateToSidebarGroupFirstOption(group);
      } else {
        this.clearSidebarFlyout();
      }
      this.markForCheck();
      return;
    }

    event.preventDefault();
    this.clearSidebarFlyout();

    this.expandedSidebarGroups = {
      home: false,
      ap: false,
      repair: false,
      expense: false,
      vendor: false,
      [group]: true,
    };
    this.expandedApMenu = null;
    this.navigateToSidebarGroupFirstOption(group);
    this.markForCheck();

    if (this.isMobileViewport()) {
      this.isMobileSidebarOpen = false;
    }
  }

  onApNavGroupParentClick(event: Event, apGroup: ApNavGroup | null): void {
    if (!apGroup) {
      return;
    }

    const groupKey = this.apGroupCollapseKey(apGroup.menu);

    if (this.isSidebarCollapsed) {
      event.preventDefault();
      event.stopPropagation();
      const opening = this.openCollapsedSidebarGroup !== groupKey;
      if (opening) {
        this.openCollapsedSidebarGroup = groupKey;
        this.updateSidebarFlyoutPosition(event.currentTarget ?? null);
        this.selectApNavGroup(apGroup);
        this.navigateToApNavGroupLanding(apGroup);
      } else {
        this.clearSidebarFlyout();
      }
      this.markForCheck();
      return;
    }

    event.preventDefault();
    this.clearSidebarFlyout();
    this.selectApNavGroup(apGroup);
    this.navigateToApNavGroupLanding(apGroup);
    this.markForCheck();

    if (this.isMobileViewport()) {
      this.isMobileSidebarOpen = false;
    }
  }

  /** Desktop: hover side-flyout for nav groups (mobile keeps click expand). */
  get useSidebarHoverFlyout(): boolean {
    return !this.isMobileViewport();
  }

  get sidebarFlyoutNgStyle(): Record<string, string> | null {
    if (!this.useSidebarHoverFlyout || !this.sidebarFlyoutPosition) {
      return null;
    }
    return this.sidebarFlyoutPosition;
  }

  private updateSidebarFlyoutPosition(groupEl: EventTarget | null): void {
    const el = groupEl as HTMLElement | null;
    if (!el?.getBoundingClientRect) {
      this.sidebarFlyoutPosition = null;
      return;
    }
    const rect = el.getBoundingClientRect();
    this.sidebarFlyoutPosition = {
      top: `${Math.round(rect.top)}px`,
      left: `${Math.round(rect.right)}px`,
    };
  }

  private cancelSidebarFlyoutClose(): void {
    if (this.sidebarFlyoutCloseTimer) {
      clearTimeout(this.sidebarFlyoutCloseTimer);
      this.sidebarFlyoutCloseTimer = null;
    }
    this.pendingSidebarFlyoutCloseKey = null;
  }

  /**
   * True when the pointer is still inside the same nav group (e.g. flyout → parent row).
   * In that case we must not schedule close — parent mouseenter will not re-fire.
   */
  private isPointerStillInNavGroup(event?: MouseEvent): boolean {
    const related = event?.relatedTarget as Node | null;
    if (!related || !event?.currentTarget) {
      return false;
    }
    const groupEl = (event.currentTarget as HTMLElement).closest(
      '.md-shell__nav-group'
    );
    return !!groupEl?.contains(related);
  }

  private scheduleSidebarFlyoutClose(groupKey: string): void {
    if (!this.useSidebarHoverFlyout) {
      return;
    }
    this.cancelSidebarFlyoutClose();
    this.pendingSidebarFlyoutCloseKey = groupKey;
    this.sidebarFlyoutCloseTimer = setTimeout(() => {
      if (this.openCollapsedSidebarGroup === this.pendingSidebarFlyoutCloseKey) {
        this.clearSidebarFlyout();
        this.markForCheck();
      }
      this.sidebarFlyoutCloseTimer = null;
      this.pendingSidebarFlyoutCloseKey = null;
    }, 220);
  }

  private clearSidebarFlyout(): void {
    this.cancelSidebarFlyoutClose();
    this.openCollapsedSidebarGroup = null;
    this.sidebarFlyoutPosition = null;
  }

  /** Keep flyout open while pointer is on the panel (after leaving parent row). */
  onSidebarFlyoutMouseEnter(groupKey: string): void {
    if (!this.useSidebarHoverFlyout) {
      return;
    }
    this.cancelSidebarFlyoutClose();
    this.openCollapsedSidebarGroup = groupKey;
    this.markForCheck();
  }

  onSidebarFlyoutMouseLeave(groupKey: string, event?: MouseEvent): void {
    if (!this.useSidebarHoverFlyout) {
      return;
    }
    // Moving back onto the parent row — stay open.
    if (this.isPointerStillInNavGroup(event)) {
      this.cancelSidebarFlyoutClose();
      return;
    }
    this.scheduleSidebarFlyoutClose(groupKey);
  }

  onApNavGroupMouseEnter(apGroup: ApNavGroup, event?: MouseEvent): void {
    if (!apGroup || !this.useSidebarHoverFlyout) {
      return;
    }
    this.cancelSidebarFlyoutClose();
    this.openCollapsedSidebarGroup = this.apGroupCollapseKey(apGroup.menu);
    this.updateSidebarFlyoutPosition(event?.currentTarget ?? null);
    this.markForCheck();
  }

  onApNavGroupMouseLeave(apGroup: ApNavGroup, event?: MouseEvent): void {
    if (!apGroup || !this.useSidebarHoverFlyout) {
      return;
    }
    if (this.isPointerStillInNavGroup(event)) {
      this.cancelSidebarFlyoutClose();
      return;
    }
    this.scheduleSidebarFlyoutClose(this.apGroupCollapseKey(apGroup.menu));
  }

  onSidebarNavGroupMouseEnter(
    group: 'home' | 'repair' | 'expense' | 'vendor',
    event?: MouseEvent
  ): void {
    if (!this.useSidebarHoverFlyout) {
      return;
    }
    this.cancelSidebarFlyoutClose();
    this.openCollapsedSidebarGroup = group;
    this.updateSidebarFlyoutPosition(event?.currentTarget ?? null);
    this.markForCheck();
  }

  onSidebarNavGroupMouseLeave(
    group: 'home' | 'repair' | 'expense' | 'vendor',
    event?: MouseEvent
  ): void {
    if (!this.useSidebarHoverFlyout) {
      return;
    }
    if (this.isPointerStillInNavGroup(event)) {
      this.cancelSidebarFlyoutClose();
      return;
    }
    this.scheduleSidebarFlyoutClose(group);
  }

  isSidebarGroupFlyoutOpen(group: string): boolean {
    return (
      this.useSidebarHoverFlyout && this.openCollapsedSidebarGroup === group
    );
  }

  isApNavGroupFlyoutOpen(menu: string): boolean {
    return (
      this.useSidebarHoverFlyout &&
      this.openCollapsedSidebarGroup === this.apGroupCollapseKey(menu)
    );
  }

  shouldRenderSidebarSubnav(
    group: 'home' | 'repair' | 'expense' | 'vendor'
  ): boolean {
    if (this.useSidebarHoverFlyout) {
      // Expanded: keep flyout in DOM; collapsed: only while hovered/open.
      return !this.isSidebarCollapsed || this.isSidebarGroupFlyoutOpen(group);
    }
    return (
      (!this.isSidebarCollapsed && this.expandedSidebarGroups[group]) ||
      this.isCollapsedGroupOpen(group)
    );
  }

  shouldRenderApSubnav(menu: string): boolean {
    if (this.useSidebarHoverFlyout) {
      return !this.isSidebarCollapsed || this.isApNavGroupFlyoutOpen(menu);
    }
    return (
      this.isApNavGroupExpanded(menu) || this.isApNavGroupCollapsedOpen(menu)
    );
  }

  /** Force exactly one AP parent open (by exact menu spelling). */
  private selectApNavGroup(apGroup: ApNavGroup): void {
    this.expandedSidebarGroups = {
      home: false,
      ap: true,
      repair: false,
      expense: false,
      vendor: false,
    };
    this.expandedApMenu = apGroup.menu;
  }

  apGroupCollapseKey(menu: string): string {
    return `ap:${this.normalizeApMenuKey(menu)}`;
  }

  /** Exact parent identity: "Account Payable" ≠ "Accounts Payable". */
  normalizeApMenuKey(menu: string | null | undefined): string {
    return (menu ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  isSameApMenu(
    a: string | null | undefined,
    b: string | null | undefined
  ): boolean {
    const left = this.normalizeApMenuKey(a);
    const right = this.normalizeApMenuKey(b);
    return Boolean(left) && left === right;
  }

  isApNavGroupExpanded(menu: string): boolean {
    // Only the matching parent opens — Account Payable vs Accounts Payable.
    return (
      !this.isSidebarCollapsed &&
      this.expandedSidebarGroups.ap &&
      this.isSameApMenu(this.expandedApMenu, menu)
    );
  }

  isApNavGroupCollapsedOpen(menu: string): boolean {
    return (
      this.isSidebarCollapsed &&
      this.openCollapsedSidebarGroup === this.apGroupCollapseKey(menu)
    );
  }

  isApNavGroupRouteActive(apGroup: ApNavGroup): boolean {
    // Keep highlight in sync with the open parent (not first URL match).
    return (
      this.expandedSidebarGroups.ap &&
      this.isSameApMenu(this.expandedApMenu, apGroup.menu)
    );
  }

  private navigateToApNavGroupLanding(apGroup: ApNavGroup): void {
    const landing = findPreferredApLandingSubMenu(apGroup.subMenus);
    if (landing) {
      const route = this.getApSubMenuRoute(landing.pageUrl);
      if (route) {
        this.navigateSidebarTo(
          [route],
          this.getApSubMenuQueryParams(landing.pageUrl) ?? {}
        );
        return;
      }
    }
    this.navigateSidebarTo(['/accounts-payable/list']);
  }

  /**
   * Programmatic sidebar navigation — reloads when the target URL matches the
   * current route so repeated clicks still work (routerLink alone skips same URL).
   */
  onSidebarNavLinkClick(
    event: Event,
    commands: unknown[],
    queryParams?: Record<string, unknown>
  ): void {
    if (
      event instanceof MouseEvent &&
      (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button === 1)
    ) {
      return;
    }
    event.preventDefault();
    this.navigateSidebarTo(commands, queryParams);
    if (this.isMobileViewport()) {
      this.isMobileSidebarOpen = false;
    }
  }

  private navigateSidebarTo(
    commands: unknown[],
    queryParams?: Record<string, unknown>
  ): void {
    this.onCollapsedSubnavItemClick();
    void this.router.navigate(commands, {
      queryParams: queryParams ?? {},
      // Replace (do not merge) so AP list filter tabs stay mutually exclusive.
      onSameUrlNavigation: 'reload',
    });
  }

  private navigateToSidebarGroupFirstOption(
    group: 'home' | 'ap' | 'repair' | 'expense' | 'vendor'
  ): void {
    switch (group) {
      case 'home': {
        const firstSubMenu = this.homepageNavGroup?.subMenus?.[0];
        if (firstSubMenu) {
          const route = this.getHomeSubMenuRoute(firstSubMenu.pageUrl);
          if (route) {
            this.navigateSidebarTo(
              [route],
              this.getHomeSubMenuQueryParams(firstSubMenu.pageUrl) ?? undefined
            );
          }
        }
        break;
      }
      case 'ap': {
        this.navigateToApNavGroupLanding(
          this.apNavGroups[0] ?? { menu: 'Accounts Payable', subMenus: [] }
        );
        break;
      }
      case 'repair': {
        const firstSubMenu = this.repairNavGroup?.subMenus?.[0];
        if (firstSubMenu) {
          this.navigateSidebarTo(
            [this.getRepairSubMenuRoute(firstSubMenu.pageUrl)],
            this.getRepairSubMenuQueryParams(firstSubMenu.pageUrl) ?? undefined
          );
        } else {
          this.navigateSidebarTo(['/repairs-and-maintenance/list']);
        }
        break;
      }
      case 'expense': {
        const firstSubMenu = this.expenseNavGroup?.subMenus?.[0];
        if (firstSubMenu) {
          this.navigateSidebarTo([this.getExpenseSubMenuRoute(firstSubMenu.pageUrl)]);
        }
        break;
      }
      case 'vendor': {
        const firstSubMenu = this.vendorNavGroup?.subMenus?.[0];
        if (firstSubMenu) {
          this.navigateSidebarTo(
            [this.getVendorSubMenuRoute(firstSubMenu.pageUrl)],
            this.getVendorSubMenuQueryParams(firstSubMenu.pageUrl) ?? undefined
          );
        } else {
          this.navigateSidebarTo(['/vendors/list'], { myvendors: '' });
        }
        break;
      }
    }
  }

  toggleMobileSidebar(): void {
    if (!this.isMobileViewport()) {
      return;
    }
    this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    if (this.isMobileSidebarOpen) {
      setTimeout(() => this.focusFirstMobileSidebarElement(), 0);
    }
  }

  closeMobileSidebar(): void {
    this.isMobileSidebarOpen = false;
  }

  onSidebarNavClick(event: Event): void {
    if (!this.isMobileViewport()) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const anchor = target.closest('a');
    if (!anchor) {
      return;
    }

    // Parent group toggles should not close sidebar on mobile.
    if (anchor.matches('.md-shell__nav-group > a')) {
      return;
    }

    this.isMobileSidebarOpen = false;
  }

  isCollapsedGroupOpen(group: string): boolean {
    return this.isSidebarCollapsed && this.openCollapsedSidebarGroup === group;
  }

  onCollapsedSubnavItemClick(): void {
    if (this.openCollapsedSidebarGroup) {
      this.clearSidebarFlyout();
      this.markForCheck();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.openCollapsedSidebarGroup) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      this.clearSidebarFlyout();
      this.markForCheck();
      return;
    }

    // Flyout is position:fixed but still a DOM child of the sidebar. Treat
    // clicks outside the active open group (parent + its flyout) as dismiss.
    const openGroup = target.closest?.('.md-shell__nav-group.is-open');
    if (!openGroup) {
      this.clearSidebarFlyout();
      this.markForCheck();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.openCollapsedSidebarGroup) {
      this.clearSidebarFlyout();
      this.markForCheck();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.openCollapsedSidebarGroup) {
      this.clearSidebarFlyout();
    }
    if (!this.isMobileViewport()) {
      this.isMobileSidebarOpen = false;
    }
    this.markForCheck();
  }

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 991.98px)').matches;
  }

  private focusFirstMobileSidebarElement(): void {
    const focusable = this.getMobileSidebarFocusableElements();
    if (!focusable.length) {
      return;
    }
    focusable[0].focus();
  }

  private trapFocusInMobileSidebar(event: KeyboardEvent): void {
    if (!this.isMobileViewport()) {
      return;
    }

    const focusable = this.getMobileSidebarFocusableElements();
    if (!focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (!active || active === first) {
        event.preventDefault();
        last.focus();
      }
      return;
    }

    if (!active || active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private getMobileSidebarFocusableElements(): HTMLElement[] {
    const root = this.mdSidebarRef?.nativeElement;
    if (!root) {
      return [];
    }

    const selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1
    );
  }

  isHomeTabActive(module: DashboardViewKey): boolean {
    const url = this.latestUrl || this.router.url || '';
    const [path, query = ''] = url.split('?');

    if (path !== '/') {
      return false;
    }

    const params = new URLSearchParams(query);
    const activeModule = (params.get('module') || '').toLowerCase();

    // Default home route (no module query) highlights the first BE submenu when available.
    if (!activeModule) {
      const defaultModule = this.getDefaultHomeModuleFromBe();
      if (defaultModule) {
        return module === defaultModule;
      }
      return module === 'overview';
    }

    return activeModule === module;
  }

  private getDefaultHomeModuleFromBe(): DashboardViewKey | null {
    const firstSubMenu = this.homepageNavGroup?.subMenus?.[0]?.pageUrl;
    if (!firstSubMenu) {
      return null;
    }

    const normalized = normalizePageUrl(firstSubMenu);
    if (!normalized.startsWith('module=')) {
      return null;
    }

    return normalized.slice('module='.length) as DashboardViewKey;
  }

  isHomeGroupRouteActive(): boolean {
    const route = (this.latestUrl || this.router.url || '').toLowerCase();
    return route === '/' || route.startsWith('/?');
  }

  isAccountsPayableGroupRouteActive(): boolean {
    const route = (this.latestUrl || this.router.url || '').toLowerCase();
    return route.startsWith('/accounts-payable');
  }

  isNavRouteExactActive(path: string): boolean {
    const target = path.toLowerCase().replace(/\/$/, '');
    return this.getCurrentRoutePath() === target;
  }

  isNavRouteActive(
    basePath: string,
    options: {
      alternateBasePaths?: string[];
      excludeSegments?: string[];
      includeCreate?: boolean;
      exactOnly?: boolean;
    } = {}
  ): boolean {
    const paths = [basePath, ...(options.alternateBasePaths ?? [])].map((p) =>
      p.toLowerCase().replace(/\/$/, '')
    );

    if (options.exactOnly) {
      const current = this.getCurrentRoutePath();
      return paths.some((p) => current === p);
    }

    return paths.some((base) => this.isNavRouteActiveForBase(base, options));
  }

  /** Roles list and every role's details page (/roles/:id). */
  isRolesRouteActive(): boolean {
    const path = this.getCurrentRoutePath();
    return path === '/roles' || path.startsWith('/roles/');
  }

  isApprovalProcessRouteActive(): boolean {
    const path = this.getCurrentRoutePath();
    if (!path.startsWith('/approval-processes') && !path.startsWith('/approval-process')) {
      return false;
    }

    const type = (this.getCurrentRouteQuery().get('type') || '').toLowerCase();
    const expenseType = ServiceRequestType.expense.toLowerCase();
    return type !== expenseType;
  }

  private getCurrentRoutePath(): string {
    return (this.latestUrl || this.router.url || '').toLowerCase().split('?')[0].replace(/\/$/, '') || '/';
  }

  private getCurrentRouteQuery(): URLSearchParams {
    const url = (this.latestUrl || this.router.url || '').toLowerCase();
    const query = url.includes('?') ? url.split('?').slice(1).join('?') : '';
    return new URLSearchParams(query);
  }

  private isNavRouteActiveForBase(
    base: string,
    options: { excludeSegments?: string[]; includeCreate?: boolean }
  ): boolean {
    const path = this.getCurrentRoutePath();

    if (path === base || path === `${base}/list`) {
      return true;
    }

    if (!path.startsWith(`${base}/`)) {
      return false;
    }

    const remainder = path.slice(base.length + 1);

    if (options.excludeSegments?.some((seg) => remainder === seg || remainder.startsWith(`${seg}/`))) {
      return false;
    }

    if (options.includeCreate && (remainder === 'create' || remainder === 'new')) {
      return true;
    }

    if (remainder === 'create' || remainder === 'new') {
      return false;
    }

    if (/^(view|details|edit)\/.+/.test(remainder)) {
      return true;
    }

    if (/^[^/]+\/(edit|detail)$/.test(remainder)) {
      return true;
    }

    return false;
  }

  isRepairGroupRouteActive(): boolean {
    const path = this.getCurrentRoutePath();
    const query = this.getCurrentRouteQuery();

    if (this.repairNavGroup?.subMenus?.length) {
      if (
        this.repairNavGroup.subMenus.some((subMenu) =>
          isRmSubMenuRouteActive(subMenu.pageUrl, path, query)
        )
      ) {
        return true;
      }

      return (
        path.startsWith('repairs-and-maintenance') &&
        path !== 'repairs-and-maintenance/list'
      );
    }

    const route = (this.latestUrl || this.router.url || '').toLowerCase();
    return (
      route.startsWith('/repairs-and-maintenance') ||
      route.startsWith('/approval-process')
    );
  }

  private syncExpandedSidebarGroupsWithRoute(url: string): void {
    const route = (url || '').toLowerCase();
    const isHomeRoute = route === '/' || route.startsWith('/?');
    const isApRoute = this.isApRoutePath(route);
    const isRepairRoute =
      route.startsWith('/repairs-and-maintenance') || route.startsWith('/approval-process');

    const isExpenseRoute = route.startsWith('/expenses');
    const isVendorRoute =
      route.startsWith('/vendors') || route.startsWith('/vendor');

    const activeApGroup = isApRoute
      ? this.resolveApNavGroupForRoute()
      : null;

    this.expandedSidebarGroups = {
      home: isHomeRoute,
      ap: isApRoute,
      repair: isRepairRoute,
      expense: isExpenseRoute,
      vendor: isVendorRoute,
    };

    // Never steal expand from the selected parent when URLs overlap across menus.
    if (isApRoute) {
      this.expandedApMenu = activeApGroup?.menu ?? this.expandedApMenu;
    } else {
      this.expandedApMenu = null;
    }

    this.clearSidebarFlyout();
  }

  /** True when URL belongs to any AP sidebar submenu (not only /accounts-payable). */
  private isApRoutePath(route: string): boolean {
    if (route.startsWith('/accounts-payable')) {
      return true;
    }

    return this.apNavGroups.some((group) =>
      group.subMenus.some((subMenu) =>
        this.apSubMenuPathMatchesRoute(subMenu.pageUrl, route)
      )
    );
  }

  /**
   * Pick the AP parent for the current URL.
   * When Account Payable + Accounts Payable share pageUrls, keep the open parent.
   */
  private resolveApNavGroupForRoute(): ApNavGroup | null {
    const activeMatches = this.apNavGroups.filter((group) =>
      group.subMenus.some((subMenu) => this.isApSubMenuActive(subMenu.pageUrl))
    );

    if (activeMatches.length === 1) {
      return activeMatches[0];
    }

    if (activeMatches.length > 1 && this.expandedApMenu) {
      const preferred = activeMatches.find((group) =>
        this.isSameApMenu(group.menu, this.expandedApMenu)
      );
      if (preferred) {
        return preferred;
      }
    }

    if (activeMatches.length > 1) {
      return this.pickMostSpecificApGroup(activeMatches);
    }

    // No active submenu highlight — path-only ownership.
    const route = (this.latestUrl || this.router.url || '').toLowerCase();
    const pathMatches = this.apNavGroups.filter((group) =>
      group.subMenus.some((subMenu) =>
        this.apSubMenuPathMatchesRoute(subMenu.pageUrl, route)
      )
    );

    if (pathMatches.length === 1) {
      return pathMatches[0];
    }

    if (pathMatches.length > 1 && this.expandedApMenu) {
      const preferred = pathMatches.find((group) =>
        this.isSameApMenu(group.menu, this.expandedApMenu)
      );
      if (preferred) {
        return preferred;
      }
    }

    if (pathMatches.length > 1) {
      return this.pickMostSpecificApGroup(pathMatches);
    }

    // Stay on the parent the user opened (click) when route is ambiguous/empty.
    if (this.expandedApMenu) {
      return (
        this.apNavGroups.find((group) =>
          this.isSameApMenu(group.menu, this.expandedApMenu)
        ) ?? null
      );
    }

    return null;
  }

  /** Prefer the group whose active submenu has the most specific query match. */
  private pickMostSpecificApGroup(groups: ApNavGroup[]): ApNavGroup {
    const currentQuery = this.getCurrentRouteQuery();
    let best: ApNavGroup = groups[0];
    let bestScore = -1;

    for (const group of groups) {
      for (const subMenu of group.subMenus) {
        if (isApSubMenuAction(subMenu.pageUrl)) {
          continue;
        }
        if (!this.isApSubMenuActive(subMenu.pageUrl)) {
          // Still score path+query ownership for path-only disambiguation.
          const { path, queryParams } = parsePageUrlParts(subMenu.pageUrl);
          const route = (this.latestUrl || this.router.url || '').toLowerCase();
          const routePath = route.split('?')[0];
          const normalizedPath = `/${path}`.replace(/\/+/g, '/').toLowerCase();
          if (
            routePath !== normalizedPath &&
            !routePath.startsWith(`${normalizedPath}/`)
          ) {
            continue;
          }
          const score = queryParams ? Object.keys(queryParams).length : 0;
          if (score > bestScore) {
            bestScore = score;
            best = group;
          }
          continue;
        }

        const { queryParams } = parsePageUrlParts(subMenu.pageUrl);
        const score = queryParams
          ? Object.keys(queryParams).filter((key) => currentQuery.has(key)).length
          : 0;
        if (score > bestScore) {
          bestScore = score;
          best = group;
        }
      }
    }

    return best;
  }

  private apSubMenuPathMatchesRoute(pageUrl: string, route: string): boolean {
    if (isApSubMenuAction(pageUrl)) {
      return false;
    }
    const path = toRouterPath(pageUrl.split('?')[0]).toLowerCase();
    return (
      route === path ||
      route.startsWith(`${path}/`) ||
      route.startsWith(`${path}?`)
    );
  }

  closeMenuOnClick(event: Event): void {
    event.stopPropagation();
    this.closeProfileDropdown();

    // Small delay to ensure the click event is processed
    setTimeout(() => {
      // Find all menu dropdowns in the navbar and close them forcefully
      const navbar = document.querySelector('.navbar-nav');
      if (navbar) {
        const allDropdowns = navbar.querySelectorAll('.menu-dropdown');
        allDropdowns.forEach((dropdown) => {
          const element = dropdown as HTMLElement;
          element.style.display = 'none';
          element.style.setProperty('display', 'none', 'important');
          // Also remove any show classes
          element.classList.remove('show');
        });
      }

      // Also close any collapse elements
      const allCollapses = document.querySelectorAll('.menu-dropdown.collapse');
      allCollapses.forEach((collapse) => {
        const element = collapse as HTMLElement;
        element.style.display = 'none';
        element.style.setProperty('display', 'none', 'important');
        element.classList.remove('show');
      });
    }, 10);
  }

  openMenuOnMouseEnter(event: MouseEvent): void {
    const target = event.currentTarget as HTMLElement;
    if (target) {
      // Find direct child menu-dropdown (not nested ones)
      const menuDropdown = target.querySelector(':scope > .menu-dropdown');
      if (menuDropdown) {
        const element = menuDropdown as HTMLElement;
        // Remove inline styles to allow CSS :hover to work
        element.style.removeProperty('display');
        // CSS will handle showing it via :hover
      }
    }
  }

  openNestedMenuOnMouseEnter(event: MouseEvent): void {
    const target = event.currentTarget as HTMLElement;
    let menuDropdown: HTMLElement | null = null;

    // If hovering over the nav-item, find its nested dropdown
    if (target.classList.contains('nav-item')) {
      menuDropdown = target.querySelector('.menu-dropdown') as HTMLElement;
    }
    // If hovering over the dropdown itself, use it directly
    else if (target.classList.contains('menu-dropdown')) {
      menuDropdown = target;
    }

    if (menuDropdown) {
      // Remove inline styles that might prevent it from showing
      menuDropdown.style.removeProperty('display');
      // Show the nested dropdown
      menuDropdown.style.display = 'block';
      menuDropdown.classList.add('show');

      // Also ensure Bootstrap collapse shows it
      const collapseInstance = bootstrap.Collapse.getInstance(menuDropdown);
      if (collapseInstance) {
        collapseInstance.show();
      }
    }
  }

  closeNestedMenuOnMouseLeave(event: MouseEvent): void {
    const target = event.currentTarget as HTMLElement;
    let menuDropdown: HTMLElement | null = null;

    // If leaving the nav-item, find its nested dropdown
    if (target.classList.contains('nav-item')) {
      menuDropdown = target.querySelector('.menu-dropdown') as HTMLElement;
    }
    // If leaving the dropdown itself, use it directly
    else if (target.classList.contains('menu-dropdown')) {
      menuDropdown = target;
    }

    if (menuDropdown) {
      menuDropdown.style.display = 'none';
      menuDropdown.style.setProperty('display', 'none', 'important');
      menuDropdown.classList.remove('show');

      // Also hide via Bootstrap collapse
      const collapseInstance = bootstrap.Collapse.getInstance(menuDropdown);
      if (collapseInstance) {
        collapseInstance.hide();
      }
    }
  }

  closeMenuOnMouseLeave(event: MouseEvent): void {
    const target = event.currentTarget as HTMLElement;
    if (target) {
      // Only close direct child menu-dropdown (not nested ones inside)
      const menuDropdown = target.querySelector(':scope > .menu-dropdown');
      if (menuDropdown) {
        const element = menuDropdown as HTMLElement;
        element.style.display = 'none';
        element.style.setProperty('display', 'none', 'important');
      }
    }
  }

  onProfileMenuOpenChange(next: boolean): void {
    if (this.profileMenuCloseTimer) {
      clearTimeout(this.profileMenuCloseTimer);
      this.profileMenuCloseTimer = null;
    }

    if (next) {
      this.dropdownCoordinator.notifyOpened(DROPDOWN_COORDINATOR_IDS.profileMenu);
      this.closeNotificationDropdown();
      this.isProfileMenuClosing = false;
      this.isProfileMenuOpen = true;
      this.markForCheck();
      return;
    }

    if (!this.isProfileMenuOpen || this.isProfileMenuClosing) {
      return;
    }

    this.isProfileMenuClosing = true;
    this.markForCheck();
    this.profileMenuCloseTimer = setTimeout(() => {
      this.isProfileMenuOpen = false;
      this.isProfileMenuClosing = false;
      this.profileMenuCloseTimer = null;
      this.markForCheck();
    }, this.profileMenuTransitionMs);
  }

  closeProfileDropdown(): void {
    this.onProfileMenuOpenChange(false);
  }

  // ngOnDestroy() {

  //   if (modalRef.content) {
  //     modalRef.content.modalClose
  //       .pipe(take(1))
  //       .subscribe((uploadedFiles: any[]) => {
  //         // Handle the uploaded files here if needed
  //         // For now, we just close the modal
  //       });
  //   }
  // }

  ngOnDestroy() {
    this.navCollapseSub?.unsubscribe();

    if (this.profileMenuCloseTimer) {
      clearTimeout(this.profileMenuCloseTimer);
      this.profileMenuCloseTimer = null;
    }
    this.cancelSidebarFlyoutClose();
    this.clearBannerAutoSlide();
    this.clearBannerTransitionTimer();
    this.ngUnSubscribe.next();

    if (typeof document !== 'undefined') {
      document.removeEventListener('keydown', this.globalKeydownListener, true);
    }

    if (this.swMessageHandler && typeof navigator !== 'undefined' && (navigator as any).serviceWorker?.removeEventListener) {
      (navigator as any).serviceWorker.removeEventListener('message', this.swMessageHandler);
      this.swMessageHandler = null;
    }

    this.ngUnSubscribe.complete();
  }
}
